/**
 * Structured Document Command System
 * 
 * This system treats documents as structured trees of nodes, not flat text.
 * All operations work with Y.js directly, making them:
 * - Position-independent (reference by ID/structure, not character position)
 * - Naturally collaborative (Y.js handles conflict resolution)
 * - Efficient at scale (no position recalculation for large docs)
 * - AI-friendly with comprehensive logging and validation
 */

import * as Y from '../../../../../shared/yjs';
import { ObjectId } from 'bson';
import { nanoid } from 'nanoid';
import { yDocumentService, createYElement, changeDecorationOrigin, yDocHighLevelApiOrigin, generateLLMId } from './yDocLowLevelAPI';
import { traceLog as emitTraceLog } from '../../../../../services/debug/traceHelpers';
import { createFileLogger } from '../../../../../utils/logger';
import { validateAndSanitizeDocContent } from '../validators';
import { ReplaceWithMarkdown } from './yDocLLMAPI';
import { StyleService } from '../../style/StyleService';
import type { FormattingRange } from '../../../types/contentItem';
import { validateFormatting } from '../../../utils/formattingValidator';
import { fuzzyMatch } from '../utils/fuzzySearch';
import { traceLog } from '../../../../../services/debug/traceHelpers';

const log = createFileLogger('YDOCHIGHLEVELAPI_SERVICE', { enabled: false });

// Create namespaced logger for this file


/**
 * Core types for document structure
 */
export type NodeId = string;
// ✅ CRITICAL: Added 'block' and 'inline' for Universal Schema compatibility
// Universal Schema uses 'block' with className attribute for paragraphs, headings, etc.
export type NodeType = 'block' | 'inline' | 'paragraph' | 'heading' | 'heading1' | 'heading2' | 'heading3' | 'heading4' | 'heading5' | 'heading6' | 'ordered_list' | 'bullet_list' | 'list' | 'list_item' | 'code_block' | 'table' | 'table_row' | 'table_cell' | 'table_header' | 'horizontal_rule' | 'hard_break' | 'image' | 'blockquote' | 'text';

/**
 * Patch log entry for tracking all document changes
 */
export interface PatchLogEntry {
  patchId: string;
  nodeId: string;
  op: 'replace' | 'insert' | 'delete' | 'transform';
  contentBefore?: any;  // Store simple PM JSON
  contentAfter?: any;
  revisionId: string;
  timestamp: number;
  status: 'pending' | 'accepted' | 'rejected';
  batchDescription?: string;
  authorId?: string;
  authorType?: 'human' | 'agent';
}

/**
 * Metadata that can be attached to any node
 */
export interface NodeMetadata {
  id: NodeId;
  type: NodeType;
  change_status?: 'new' | 'old' | 'none';
  revision_id?: string;
  attrs?: Record<string, any>;
  docId?: string; // Document ID for prefixed node generation
}

/**
 * Context provided to every command - everything needed to manipulate the document
 */
export interface CommandContext {
  yxml: Y.XmlFragment;        // The Y.js document fragment
  doc: Y.Doc;                 // The Y.js document (for transactions)
  docId: string;              // Document identifier
  userId?: string;            // User performing the operation
  metadata?: {
    windowId?: string;
    spaceId?: string;
    editorView?: any;
    suggestionMode?: boolean;
    [key: string]: any;
  };
}

/**
 * Base interface for all document commands
 */
export interface DocumentCommand {
  /**
   * Execute the command in the given context
   * Returns true if the command made changes, false otherwise
   */
  execute(context: CommandContext): boolean;
  
  /**
   * Optional: Get a human-readable description of what this command will do
   */
  describe?(): string;
  
  /**
   * Optional: Validate command parameters before execution
   */
  validate?(context: CommandContext): { isValid: boolean; errors: string[] };
}

/**
 * Result of finding a node in the document
 */
export interface NodeLocation {
  element: Y.XmlElement;
  index: number;  // Index within parent
  parent: Y.XmlFragment | Y.XmlElement;
}

/**
 * Patch logging utility for tracking document changes
 */
export class PatchLogger {
  /**
   * Append to revision_meta for a given revision. Creates the entry if missing.
   */
  private static appendRevisionMeta(
    context: CommandContext,
    revisionId: string,
    { oldTextAppend, newTextAppend }: { oldTextAppend?: string; newTextAppend?: string }
  ): void {
    try {
      const metaMap = context.doc.getMap('revision_meta');
      const existing = (metaMap.get(revisionId) as any) || {};
      const oldParts: string[] = [];
      const newParts: string[] = [];
      if (typeof existing.oldText === 'string' && existing.oldText.length) oldParts.push(existing.oldText);
      if (typeof oldTextAppend === 'string' && oldTextAppend.trim().length) oldParts.push(oldTextAppend.trim());
      if (typeof existing.newText === 'string' && existing.newText.length) newParts.push(existing.newText);
      if (typeof newTextAppend === 'string' && newTextAppend.trim().length) newParts.push(newTextAppend.trim());
      if (oldParts.length === 0 && newParts.length === 0) return;
      // Deduplicate segments
      const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));
      metaMap.set(revisionId, {
        oldText: uniq(oldParts).join('\n'),
        newText: uniq(newParts).join('\n'),
        timestamp: Date.now(),
      } as any);
    } catch (e) {
      log.warn('[PATCH_LOG] Failed to append revision_meta', e);
    }
  }

  /**
   * Recompute revision_meta by scanning the Y.Xml tree for this revision.
   * Captures text from nodes with attrs {revision_id, change_status} and diff_type markers.
   */
  static recomputeRevisionMeta(context: CommandContext, revisionId: string): void {
    try {
      const oldParts: string[] = [];
      const newParts: string[] = [];
      const gather = (fragment: Y.XmlFragment | Y.XmlElement) => {
        for (let i = 0; i < fragment.length; i++) {
          const child = fragment.get(i);
          if (child instanceof Y.XmlElement) {
            const attrs = child.getAttributes ? child.getAttributes() : {};
            const rid = attrs?.revision_id;
            const cs = attrs?.change_status;
            const diffType = String(attrs?.diff_type || '');
            if (String(rid) === String(revisionId)) {
              const txt = DocumentUtils.getTextContent(child) || '';
              if (cs === 'old' || diffType.includes('agent_delete')) oldParts.push(txt);
              if (cs === 'new' || diffType.includes('agent_insert')) newParts.push(txt);
            }
            gather(child);
          }
        }
      };
      gather(context.yxml);
      const metaMap = context.doc.getMap('revision_meta');
      const oldText = oldParts.filter(Boolean).join('\n');
      const newText = newParts.filter(Boolean).join('\n');
      if (!oldText && !newText) {
        try { metaMap.delete(revisionId); } catch {}
        return;
      }
      metaMap.set(revisionId, { oldText, newText, timestamp: Date.now() } as any);
    } catch (e) {
      log.warn('[PATCH_LOG] Failed to recompute revision_meta', e);
    }
  }
  /**
   * Log a patch operation to the Y.js document
   */
  static logPatch(context: CommandContext, entry: Omit<PatchLogEntry, 'timestamp'>): void {
    const patchLog = context.doc.getMap('patch_log');
    const fullEntry: PatchLogEntry = {
      ...entry,
      timestamp: Date.now()
    };
    
    patchLog.set(entry.patchId, fullEntry);
    // log.log('[PATCH_LOG] Logged patch:', fullEntry);

    // Opportunistically update revision_meta for this revision
    try {
      let oldTextAppend: string | undefined;
      let newTextAppend: string | undefined;
      if (fullEntry.op === 'delete') {
        oldTextAppend = (fullEntry as any).contentBefore?.text;
        if (!oldTextAppend || oldTextAppend.length === 0) {
          // Try to read current element text by nodeId
          const loc = DocumentUtils.findNodeById(context.yxml, fullEntry.nodeId);
          if (loc?.element) oldTextAppend = DocumentUtils.getTextContent(loc.element);
        }
      } else if (fullEntry.op === 'insert') {
        newTextAppend = (fullEntry as any).contentAfter?.text;
        if (!newTextAppend || newTextAppend.length === 0) {
          const loc = DocumentUtils.findNodeById(context.yxml, fullEntry.nodeId);
          if (loc?.element) newTextAppend = DocumentUtils.getTextContent(loc.element);
        }
      }
      if (oldTextAppend || newTextAppend) {
        this.appendRevisionMeta(context, fullEntry.revisionId, { oldTextAppend, newTextAppend });
      }
    } catch {}
  }

  /**
   * Apply dual marking - both block-level attributes and inline marks
   */
  static applyDualMarking(
    element: Y.XmlElement, 
    operation: 'insert' | 'replace' | 'delete', 
    revisionId: string,
    suggestionMode: boolean = true
  ): void {
    // log.log('🔍 [PATCHLOGGER_START] applyDualMarking called:', {
    //   operation,
    //   revisionId,
    //   suggestionMode,
    //   elementId: element.getAttribute('id'),
    //   elementType: element.getAttribute('type')
    // });

    if (suggestionMode) {
      // Block-level tracking (for AI navigation and filtering)
      const targetChangeStatus = operation === 'delete' ? 'old' : 'new';
      
      element.setAttribute('change_status', targetChangeStatus);
      element.setAttribute('revision_id', revisionId);
      element.setAttribute('diff_type', `agent_${operation}`);

    } else {
      log.log('  ⚠️ [PATCHLOGGER_SKIP] suggestionMode is false, skipping marking');
    }
    // log.log('🔍 [PATCHLOGGER_END] applyDualMarking complete');
    // Note: Inline marks (insertion/deletion) are handled by ProseMirror textDiffService
  }

  /**
   * Get all pending patches from the log
   */
  static getPendingPatches(context: CommandContext): PatchLogEntry[] {
    const patchLog = context.doc.getMap('patch_log');
    const patches: PatchLogEntry[] = [];

    // Opportunistic compaction: drop stale entries that no longer correspond to live suggestion marks
    const toDelete: string[] = [];

    patchLog.forEach((entry: any, key: string) => {
      if (!entry || typeof entry !== 'object') return;

      // Only surface pending entries to callers
      if (entry.status !== 'pending') return;

      // Validate that the node still exists and is marked with this revision
      try {
        const loc = DocumentUtils.findNodeById(context.yxml, String(entry.nodeId || ''));
        const el = loc?.element;
        const revisionId = el && typeof (el as any).getAttribute === 'function' ? (el as any).getAttribute('revision_id') : undefined;
        const changeStatus = el && typeof (el as any).getAttribute === 'function' ? (el as any).getAttribute('change_status') : undefined;

        const looksValid = !!el && revisionId === entry.revisionId && (changeStatus === 'new' || changeStatus === 'old');

        if (looksValid) {
          patches.push(entry as PatchLogEntry);
        } else {
          // Stale pending entry (node removed or attributes cleared) → delete from log
          toDelete.push(key);
        }
      } catch {
        // On any failure, conservatively delete the stale entry
        toDelete.push(key);
      }
    });

    if (toDelete.length > 0) {
      toDelete.forEach((k) => patchLog.delete(k));
    }

    return patches.sort((a, b) => a.timestamp - b.timestamp);
  }

  /**
   * Accept a patch by ID
   */
  static acceptPatch(context: CommandContext, patchId: string): boolean {
    const patchLog = context.doc.getMap('patch_log');
    const entry = patchLog.get(patchId) as PatchLogEntry;
    
    if (!entry || entry.status !== 'pending') {
      return false;
    }

    // Perform acceptance semantics based on operation type
    const nodeLocation = DocumentUtils.findNodeById(context.yxml, entry.nodeId);
    context.doc.transact(() => {
      if (nodeLocation) {
        if (entry.op === 'insert') {
          // Accept new content: clear suggestion attrs
          nodeLocation.element.removeAttribute('change_status');
          nodeLocation.element.removeAttribute('diff_type');
          nodeLocation.element.removeAttribute('revision_id');
        } else if (entry.op === 'delete') {
          // Accept deletion: physically remove the node
          nodeLocation.parent.delete(nodeLocation.index, 1);
        }
      }
      // Update patch status
      patchLog.set(patchId, { ...entry, status: 'accepted' });
    }, changeDecorationOrigin);
    
    log.log('[PATCH_LOG] Accepted patch:', patchId);
    // Keep meta consistent after state change
    try { this.recomputeRevisionMeta(context, entry.revisionId); } catch {}
    return true;
  }

  /**
   * Reject a patch by ID
   */
  static rejectPatch(context: CommandContext, patchId: string): boolean {
    const patchLog = context.doc.getMap('patch_log');
    const entry = patchLog.get(patchId) as PatchLogEntry;
    
    if (!entry || entry.status !== 'pending') {
      return false;
    }

    // Perform rejection semantics based on operation type
    const nodeLocation = DocumentUtils.findNodeById(context.yxml, entry.nodeId);
    context.doc.transact(() => {
      if (nodeLocation) {
        if (entry.op === 'insert') {
          // Reject new content: remove inserted node
          nodeLocation.parent.delete(nodeLocation.index, 1);
        } else if (entry.op === 'delete') {
          // Reject deletion: clear suggestion attrs on old node
          nodeLocation.element.removeAttribute('change_status');
          nodeLocation.element.removeAttribute('diff_type');
          nodeLocation.element.removeAttribute('revision_id');
        }
      }
      // Update patch status
      patchLog.set(patchId, { ...entry, status: 'rejected' });
    }, changeDecorationOrigin);

    log.log('[PATCH_LOG] Rejected patch:', patchId);
    // Keep meta consistent after state change
    try { this.recomputeRevisionMeta(context, entry.revisionId); } catch {}
    return true;
  }
}

/**
 * Enhanced utility functions for working with the document tree
 */
export class DocumentUtils {
  /**
   * Find a node by its ID, returning its location in the tree
   * Enhanced with better logging and ID format handling
   */
  static findNodeById(yxml: Y.XmlFragment | Y.XmlElement, nodeId: NodeId): NodeLocation | null {
    // log.log('[AI_TOOL_LOG] [DocumentUtils] Looking for node ID:', nodeId);
    
    // Use centralized service for consistency
    const result = yDocumentService.findNodeById(
      { getXmlFragment: () => yxml } as any,
      nodeId,
      { logPrefix: '[DocumentUtils.findNodeById]' }
    );
    
    if (!result) {
      // log.log('[AI_TOOL_LOG] [DocumentUtils] Node not found:', nodeId);
      return null;
    }
    
    // log.log('[AI_TOOL_LOG] [DocumentUtils] Found node:', {
    //   nodeId,
    //   elementId: result.element.getAttribute('id'),
    //   index: result.index
    // });
    
    return result;
  }
  
  /**
   * Create a new element with standard metadata
   * Enhanced with AI-specific logging and validation
   */
  static createElement(
    type: NodeType, 
    content: string = '', 
    metadata: Partial<NodeMetadata> = {}
  ): Y.XmlElement {
    log.log('[AI_TOOL_LOG] [DocumentUtils] Creating element:', { 
      type, 
      content: typeof content === 'string' ? content.substring(0, 100) + (content.length > 100 ? '...' : '') : content,
      contentType: typeof content,
      contentLength: typeof content === 'string' ? content.length : 'N/A',
      metadata 
    });
    
    // ✅ DEBUG: Validate content is a string
    if (typeof content !== 'string') {
      log.error('[AI_TOOL_LOG] [DocumentUtils] CONTENT TYPE ERROR:', {
        expected: 'string',
        actual: typeof content,
        contentValue: content,
        contentStringified: String(content)
      });
      
      // Convert to string (String(object) naturally returns '[object Object]' for valid objects)
      content = String(content);
    }
    
    // Check if content is a literal '[object Object]' string (not object serialization)
    if (typeof content === 'string' && content === '[object Object]') {
      log.error('[AI_TOOL_LOG] [DocumentUtils] 🚨 CRITICAL: Detected literal [object Object] string, replacing with fallback');
      content = '[Content processing error - please try again]';
    }
    
    // REFACTORED: Use node types directly (no mapping needed!)
    // UniversalSchema now has specific node types (paragraph, heading, etc.)
    const actualType = type;
    const className = metadata.attrs?.className || (metadata as any).className;
    
    // Use centralized factory for consistency
    const element = createYElement(actualType, {
      id: metadata.id,
      content: content || undefined,
      docId: metadata.docId, // Pass docId for proper prefixed node generation
      attributes: {
        ...(className && { className }),  // ✅ Add className for Universal Schema (if not already set)
        ...(metadata.change_status && { change_status: metadata.change_status }),
        ...(metadata.revision_id && { revision_id: metadata.revision_id }),
        ...metadata.attrs  // ✅ Metadata attrs can override className if needed
      },
      logPrefix: '[DocumentUtils.createElement]'
    });
    
    // ✅ DEBUG: Log creation without reading attributes (Y.js doesn't allow attribute reading until added to document)
    log.log('[AI_TOOL_LOG] [DocumentUtils] Element created successfully:', {
      type: type,
      hasContent: !!content,
      hasMetadata: !!metadata,
      elementType: element.constructor.name,
      willBeAddedToDocument: true
    });
    
    return element;
  }

  /**
   * Create element with formatted text content
   */
  static createElementWithFormatting(
    type: NodeType,
    content: string = '',
    formatting?: FormattingRange[],
    metadata: Partial<NodeMetadata> = {}
  ): Y.XmlElement {
    log.log('[AI_TOOL_LOG] [DocumentUtils] Creating element with formatting:', { 
      type, 
      contentLength: content.length,
      formattingCount: formatting?.length || 0,
      hasFormatting: !!(formatting && formatting.length > 0),
      metadata 
    });
    
    // If no formatting, use regular createElement
    if (!formatting || formatting.length === 0) {
      return this.createElement(type, content, metadata);
    }
    
    // ✅ VALIDATE FORMATTING: Check for invalid ranges before applying
    if (content && formatting.length > 0) {
      const validation = validateFormatting(content, formatting);
      
      if (!validation.isValid) {
        log.error('[AI_TOOL_LOG] [DocumentUtils] Invalid formatting ranges detected:', {
          errors: validation.errors,
          type,
          contentLength: content.length,
          formattingCount: formatting.length
        });
        
        // Filter out invalid ranges and continue with valid ones
        const originalCount = formatting.length;
        formatting = formatting.filter((range, index) => {
          const isValid = range.start >= 0 && 
                         range.end <= content.length && 
                         range.start < range.end;
          if (!isValid) {
            log.warn('[AI_TOOL_LOG] [DocumentUtils] Skipping invalid range:', { index, range });
          }
          return isValid;
        });
        
        // ✅ FIX: Warn if ALL formatting was lost
        if (formatting.length === 0 && originalCount > 0) {
          log.error('[AI_TOOL_LOG] [DocumentUtils] 🚨 ALL FORMATTING LOST - all ranges were invalid!', {
            originalRangeCount: originalCount,
            validRangeCount: 0,
            contentLength: content.length,
            contentPreview: content.substring(0, 100),
            recommendedAction: 'Check formatting generation logic in markdownToContentArray'
          });
        }
      }
      
      if (validation.warnings.length > 0) {
        log.warn('[AI_TOOL_LOG] [DocumentUtils] Formatting warnings:', validation.warnings);
      }
    }
    
    // Create element without content first, we'll add formatted content manually
    const element = createYElement(type, {
      id: metadata.id,
      docId: metadata.docId,
      attributes: {
        ...(metadata.change_status && { change_status: metadata.change_status }),
        ...(metadata.revision_id && { revision_id: metadata.revision_id }),
        ...metadata.attrs
      },
      logPrefix: '[DocumentUtils.createElementWithFormatting]'
    });
    
    // Add formatted text content
    if (content) {
      // 🔍 DEBUG: Check for indentation before Y.js insertion
      const hasLeadingSpaces = /^\s+/.test(content);
      if (hasLeadingSpaces) {
        const leadingCount = content.match(/^\s+/)?.[0].length || 0;
        const leadingChars = content.substring(0, leadingCount);
        const leadingCharCodes = leadingChars.split('').map(c => c.charCodeAt(0));
        
        // Analyze what type of whitespace
        const hasTab = leadingCharCodes.includes(9);
        const hasSpace = leadingCharCodes.includes(32);
        const isOnlyTab = leadingCharCodes.length === 1 && leadingCharCodes[0] === 9;
        const isOnlySpaces = leadingCharCodes.every(c => c === 32);
        
        log.log('✅ [TAB_DEBUG] [Y.js-Insert] Text with leading whitespace entering Y.js:', {
          type,
          leadingCharCount: leadingCount,
          leadingCharCodes: leadingCharCodes,
          isSingleTab: isOnlyTab,
          isFourSpaces: isOnlySpaces && leadingCount === 4,
          hasTab: hasTab,
          hasSpace: hasSpace,
          contentPreview: JSON.stringify(content.substring(0, 50)),
          contentLength: content.length,
          firstCharCode: content.charCodeAt(0),
          firstCharName: content.charCodeAt(0) === 9 ? 'TAB' : (content.charCodeAt(0) === 32 ? 'SPACE' : 'OTHER'),
          first10Chars: content.substring(0, 10).split('').map((c, i) => ({
            char: c === '\t' ? '\\t' : (c === ' ' ? 'SPACE' : c),
            code: c.charCodeAt(0),
            isTab: c.charCodeAt(0) === 9,
            isSpace: c.charCodeAt(0) === 32
          })),
          willInsertIntoDoc: true,
          note: 'Compare SINGLE TAB (code=9, count=1) vs FOUR SPACES (code=32, count=4)'
        });
      } else if (type === 'paragraph') {
        // Log paragraphs WITHOUT indentation too (to see what we're getting)
        log.log('🔍 [TAB_DEBUG] [Y.js-Insert] Paragraph WITHOUT leading whitespace:', {
          type,
          contentPreview: JSON.stringify(content.substring(0, 50)),
          firstCharCode: content.charCodeAt(0),
          note: 'Normal paragraph with no indentation'
        });
      }
      
      const textNode = new Y.XmlText();
      const contentLength = content.length;  // ✅ Track length separately
      
      // First insert the text content
      textNode.insert(0, content);
      
      log.log('[AI_TOOL_LOG] [DocumentUtils] Applying formatting to new text node:', {
        contentLength: contentLength,
        formattingCount: formatting.length,
        formattingRanges: formatting.map(f => ({start: f.start, end: f.end, marks: Object.keys(f.marks)}))
      });
      
      // Apply each formatting range
      for (const format of formatting) {
        if (format.start >= 0 && format.end <= contentLength && format.start < format.end) {  // ✅ Use tracked length
          log.log('[AI_TOOL_LOG] [DocumentUtils] Applying format:', {
            start: format.start,
            end: format.end,
            marks: format.marks,
            textSegment: content.substring(format.start, format.end)
          });
          
          try {
            textNode.format(format.start, format.end - format.start, format.marks);
            
            log.log('[AI_TOOL_LOG] [DocumentUtils] ✅ Format applied successfully');
          } catch (error) {
            log.error('[AI_TOOL_LOG] [DocumentUtils] ❌ Format application failed:', error);
          }
        } else {
          log.warn('[AI_TOOL_LOG] [DocumentUtils] Invalid formatting range:', {
            start: format.start,
            end: format.end,
            contentLength: contentLength  // ✅ Use tracked length
          });
        }
      }
      
      // ✅ CRITICAL FIX: For UniversalSchema, wrap text in inline element!
      // UniversalSchema requires: block → inline → text (3 levels)
      // REFACTORED: 2-level structure (direct text insertion)
      element.insert(0, [textNode]);
      
      log.log('[AI_TOOL_LOG] [DocumentUtils] ✅ Element created (2-level structure):', {
        type,
        elementCreated: true
      });
    }
    
    return element;
  }
  
  /**
   * Get the text content of an element (recursive)
   */
  static getTextContent(element: Y.XmlElement | Y.XmlFragment): string {
    return yDocumentService.extractTextContent(element);
  }
  
  /**
   * Validate node ID format and existence
   */
  static validateNodeId(nodeId: NodeId): { isValid: boolean; error?: string } {
    if (!nodeId || nodeId.trim() === '') {
      return { isValid: false, error: 'Node ID cannot be empty' };
    }
    
    if (nodeId === 'undefined' || nodeId === 'null') {
      return { isValid: false, error: 'Node ID cannot be undefined or null' };
    }
    
    // Check if it looks like a proper node ID format
    const validNodeIdPattern = /^node_[a-z]+_[a-z]+_[a-z]+$/;
    if (!validNodeIdPattern.test(nodeId)) {
      return { 
        isValid: false, 
        error: `Invalid node ID format: "${nodeId}". Expected format: "node_word_word_word" (e.g., "node_atlas_mesa_quartz"). Use doc_read_v2 to get valid node IDs.` 
      };
    }
    
    return { isValid: true };
  }
  
  /**
   * Validate location parameter
   */
  static validateLocation(location: any): { isValid: boolean; error?: string } {
    if (!location || typeof location !== 'object') {
      return { isValid: false, error: 'Location must be an object' };
    }
    
    if ('after' in location) {
      const validation = this.validateNodeId(location.after);
      if (!validation.isValid) {
        return { isValid: false, error: `Invalid 'after' node ID: ${validation.error}` };
      }
    }
    
    if ('before' in location) {
      const validation = this.validateNodeId(location.before);
      if (!validation.isValid) {
        return { isValid: false, error: `Invalid 'before' node ID: ${validation.error}` };
      }
    }
    
    if ('at' in location) {
      if (location.at !== 'start' && location.at !== 'end') {
        return { isValid: false, error: "Location 'at' must be 'start' or 'end'" };
      }
    }
    
    return { isValid: true };
  }
}

/**
 * Primitive Commands - The fundamental operations
 */

/**
 * Insert content into the document at a structural location
 */
export class Insert implements DocumentCommand {
  private revisionId: string;
  private batchDescription: string;
  private suggestionMode: boolean;
  private insertedNodeIds: string[] = [];
  
  constructor(
    private content: Array<{
      type: NodeType;
      text?: string;
      content?: any[];  // ✅ FIX: Allow nested content for lists
      src?: string;     // ✅ FIX: For image nodes
      attrs?: any;      // ✅ FIX: For attributes like image src, heading level, etc.
      metadata?: Partial<NodeMetadata>;
      formatting?: Array<{
        start: number;
        end: number;
        marks: Record<string, any>;
      }>;
    }>,
    private location: 
      | { after: NodeId }           // Insert after a specific node
      | { before: NodeId }          // Insert before a specific node
      | { at: 'start' | 'end' }     // Insert at document start/end
      | { index: number },          // Insert at specific index
    options: {
      revisionId?: string;
      batchDescription?: string;
      suggestionMode?: boolean;
    } = {}
  ) {
    this.revisionId = options.revisionId || `rev_${nanoid(10)}`;
    this.batchDescription = options.batchDescription || 'Insert operation';
    this.suggestionMode = options.suggestionMode !== false; // Default to true
    log.log('[AI_TOOL_LOG] [Insert] Command created:', {
      contentCount: content.length,
      location,
      revisionId: this.revisionId,
      batchDescription: this.batchDescription,
      suggestionMode: this.suggestionMode
    });
  }
  
  validate(context: CommandContext): { isValid: boolean; errors: string[] } {
    const { errors } = validateAndSanitizeDocContent(this.content);
    const locationValidation = DocumentUtils.validateLocation(this.location);

    const allErrors = [...errors, ...locationValidation.error ? [locationValidation.error] : []];
    
    return { isValid: allErrors.length === 0, errors: allErrors };
  }
  
  /**
   * Map LLM-friendly heading types (heading1, heading2, etc.) to semantic heading + level
   */
  private mapHeadingTypes(item: any): any {
    // Check if it's a heading type that needs mapping
    const headingMatch = item.type.match(/^heading([1-6])$/);
    if (headingMatch) {
      const level = parseInt(headingMatch[1]);
      return {
        ...item,
        type: 'heading',
        attributes: {
          level,
          ...(item.attributes || {})
        }
      };
    }
    
    // ✅ FIX: Handle type: "heading" with separate level property
    if (item.type === 'heading' && item.level !== undefined) {
      return {
        ...item,
        attributes: {
          level: item.level,
          ...(item.attributes || {})
        }
      };
    }
    
    // ✅ FIX: Handle standard ProseMirror format: type: "heading" with attrs.level
    if (item.type === 'heading' && item.attrs?.level !== undefined) {
      return {
        ...item,
        attributes: {
          level: item.attrs.level,
          ...(item.attributes || {}),
          ...(item.attrs || {})
        }
      };
    }
    
    // Return unchanged if not a heading type needing mapping
    return item;
  }
  
  execute(context: CommandContext): boolean {
    log.log('[AI_TOOL_LOG] [Insert] Executing command:', {
      contentCount: this.content.length,
      location: this.location,
      revisionId: this.revisionId
    });
    
    // Validate before execution
    const validation = this.validate(context);
    if (!validation.isValid) {
      log.error('[AI_TOOL_LOG] [Insert] Validation failed:', validation.errors);
      return false;
    }
    
    const { yxml, doc } = context;
    const suggestionMode = this.suggestionMode;
    
    // Precompute insertion index to derive inherited styles before creating elements
    let provisionalInsertIndex: number;
    if ('after' in this.location) {
      const found = DocumentUtils.findNodeById(yxml, this.location.after);
      provisionalInsertIndex = found ? found.index + 1 : yxml.length;
    } else if ('before' in this.location) {
      const found = DocumentUtils.findNodeById(yxml, this.location.before);
      provisionalInsertIndex = found ? found.index : yxml.length;
    } else if ('at' in this.location) {
      provisionalInsertIndex = this.location.at === 'start' ? 0 : yxml.length;
    } else {
      provisionalInsertIndex = Math.max(0, Math.min(this.location.index, yxml.length));
    }

    // Determine inherited style_id from neighbors or document defaults
    // CRITICAL FIX: Don't inherit HEADING STYLES!
    // Problem: When user sets line to "Heading 1" via dropdown, ProseMirror creates
    // a PARAGRAPH element with style_id="heading_1", not an actual heading element.
    // So we must check the STYLE_ID VALUE, not just the element type.
    let inheritedStyleId: string | undefined;
    let inheritedFromType: string | undefined;
    let inheritedStyleName: string | undefined;
    try {
      const neighborPrev = provisionalInsertIndex > 0 ? yxml.get(provisionalInsertIndex - 1) : null;
      const neighborNext = provisionalInsertIndex < yxml.length ? yxml.get(provisionalInsertIndex) : null;
      
      const getStyleId = (el: any): string | undefined => {
        try {
          return (el && el.getAttribute && typeof el.getAttribute === 'function') ? String(el.getAttribute('style_id') || '') || undefined : undefined;
        } catch { return undefined; }
      };
      
      const getNodeType = (el: any): string | undefined => {
        try {
          return el ? (el.nodeName || el.getAttribute?.('type')) : undefined;
        } catch { return undefined; }
      };
      
      /**
       * Check if a style_id represents a heading style.
       * Heading styles have IDs like "heading_1", "heading_2", etc.
       * We must reject these to prevent paragraphs from looking like headings.
       * 
       * Actual system heading style IDs:
       * - heading_1, heading_2, heading_3, heading_4, heading_5, heading_6
       */
      const isHeadingStyle = (styleId: string): boolean => {
        if (!styleId) return false;
        const lowerStyleId = styleId.toLowerCase();
        return (
          lowerStyleId.startsWith('heading_') ||
          lowerStyleId.startsWith('heading-') ||
          lowerStyleId.match(/^heading_?\d+$/) !== null
          // Note: Intentionally NOT using .includes('heading') - too broad
          // Would match "subheading", "blockheading", etc. (false positives)
        );
      };
      
      // Check previous neighbor
      const prevType = getNodeType(neighborPrev);
      const prevStyleId = getStyleId(neighborPrev);
      
      // Check next neighbor
      const nextType = getNodeType(neighborNext);
      const nextStyleId = getStyleId(neighborNext);
      
      // CRITICAL: Reject heading styles!
      // Even if element is a paragraph, if it has heading_1/heading_2 style, don't inherit.
      const canInheritFromPrev = prevStyleId && !isHeadingStyle(prevStyleId);
      const canInheritFromNext = nextStyleId && !isHeadingStyle(nextStyleId);
      
      if (canInheritFromPrev) {
        inheritedStyleId = prevStyleId;
        inheritedFromType = prevType;
        inheritedStyleName = prevStyleId;
      } else if (canInheritFromNext) {
        inheritedStyleId = nextStyleId;
        inheritedFromType = nextType;
        inheritedStyleName = nextStyleId;
      }
      
      
      if (!inheritedStyleId) {
        const registry = StyleService.read(context.doc);
        const docStyleMap = context.doc.getMap('doc_style');
        const defaultFont = docStyleMap?.get('fontFamily');
        
        
        if (typeof defaultFont === 'string' && defaultFont.trim().length > 0) {
          const paras: any = (registry as any).paragraph || {};
          const match = Object.values(paras).find((s: any) => (s?.fontFamily || '').trim() === defaultFont.trim()) as any;
          if (match && match.id && !isHeadingStyle(match.id)) {
            inheritedStyleId = match.id as string;
          }
        }
      }
    } catch {}

    /**
     * Apply inherited style to elements that should inherit presentational attributes.
     * 
     * CRITICAL TWO-PART CHECK:
     * 1. Only PARAGRAPHS and LIST_ITEMS can receive inherited styles (NOT heading elements)
     * 2. Only inherit NON-HEADING styles (reject style_id like "heading_1", "heading_2", etc.)
     * 
     * This prevents two bugs:
     * - Heading elements inheriting styles (overrides their semantic type)
     * - Paragraphs inheriting heading styles (makes paragraphs look like headings)
     * 
     * CRITICAL INSIGHT: When user sets line to "Heading 1" via dropdown, ProseMirror
     * creates a PARAGRAPH element with style_id="heading_1", NOT a heading element!
     * So we must check the STYLE_ID VALUE, not just the element type.
     * 
     * If cursor is on a line with heading_1 style and AI sends plain text, the
     * paragraphs will render as normal paragraphs (not as headings).
     */
    const withInheritedStyle = (attrs: any, nodeType: string) => {
      // Check if element already has an explicit style_id - always respect it
      const existing = (attrs && (attrs.style_id || attrs.styleId)) as string | undefined;
      if (existing) {
        return { ...(attrs || {}), style_id: existing };
      }
      
      // ONLY paragraphs and list_items should inherit style from neighbors
      // Headings, code blocks, blockquotes, and other semantic elements should NOT
      const shouldInheritStyle = (nodeType === 'paragraph' || nodeType === 'list_item');
      
      if (shouldInheritStyle && inheritedStyleId) {
        return { ...(attrs || {}), style_id: inheritedStyleId };
      }
      
      // No inheritance - return attrs as-is
      return (attrs || {});
    };

    // Helper: get document default styling for inheritance
    interface DocumentDefaults {
      fontFamily?: string;
      fontSize?: number;
      textColor?: string;  // ← Document default text color
    }
    
    const documentDefaults: DocumentDefaults = {};
    try {
      // Read document-level defaults from doc_style map
      const docStyleMap = context.doc.getMap('doc_style');
      
      // Font family
      const fam = docStyleMap?.get('fontFamily');
      if (typeof fam === 'string' && fam.trim().length > 0) {
        documentDefaults.fontFamily = fam;
      }
      
      // Font size
      const size = docStyleMap?.get('fontSize');
      if (typeof size === 'number' && size > 0) {
        documentDefaults.fontSize = size;
      }
      
      // Text color - try doc_style first, then docDefaults.run.color
      let color = docStyleMap?.get('textColor');
      let colorSource = 'none';
      if (color) {
        colorSource = 'doc_style.textColor';
      } else {
        // Fallback to docDefaults.run.color (for Word imports)
        const registry = StyleService.read(context.doc);
        color = registry.docDefaults?.run?.color;
        if (color) {
          colorSource = 'docDefaults.run.color';
        }
      }
      if (typeof color === 'string' && color.trim().length > 0) {
        documentDefaults.textColor = color;
      }
      
      // If no doc-level defaults but we have an inherited style_id, get from style
      if (inheritedStyleId && (!documentDefaults.fontFamily || !documentDefaults.fontSize || !documentDefaults.textColor)) {
        const registry = StyleService.read(context.doc);
        const style = (registry.paragraph || {})[inheritedStyleId];
        if (style) {
          if (!documentDefaults.fontFamily && style.fontFamily) {
            documentDefaults.fontFamily = style.fontFamily;
          }
          if (!documentDefaults.fontSize && style.fontSize) {
            documentDefaults.fontSize = style.fontSize;
          }
          // Note: ParagraphStyle doesn't have textColor, so we skip this
          // Color inheritance comes from doc_style or docDefaults.run.color
        }
      }
      
      // 🔧 CRITICAL NEW FEATURE: Inherit from previous paragraph
      // If still no defaults, look at the paragraph immediately before insertion point
      // This enables "sticky" inheritance where properties persist across AI-inserted paragraphs
      if (!documentDefaults.fontFamily || !documentDefaults.fontSize || !documentDefaults.textColor) {
        try {
          const yxml = context.yxml;
          const yxmlLength = yxml.length;
          
          // Determine which paragraph to inherit from based on insertion location
          let paragraphToInheritFrom: Y.XmlElement | null = null;
          
          if ('after' in this.location) {
            // Inserting after a specific node - inherit from that node
            const targetNode = DocumentUtils.findNodeById(yxml, this.location.after);
            if (targetNode && targetNode.element instanceof Y.XmlElement && targetNode.element.nodeName === 'paragraph') {
              paragraphToInheritFrom = targetNode.element;
            }
          } else if ('before' in this.location) {
            // Inserting before a specific node - inherit from previous sibling
            const targetNode = DocumentUtils.findNodeById(yxml, this.location.before);
            if (targetNode && targetNode.index > 0) {
              const prevElement = yxml.get(targetNode.index - 1);
              if (prevElement instanceof Y.XmlElement && prevElement.nodeName === 'paragraph') {
                paragraphToInheritFrom = prevElement;
              }
            }
          } else if (yxmlLength > 0) {
            // Inserting at start/end or no specific location - use last paragraph
            const lastElement = yxml.get(yxmlLength - 1);
            if (lastElement instanceof Y.XmlElement && lastElement.nodeName === 'paragraph') {
              paragraphToInheritFrom = lastElement;
            }
          }
          
          // If we found a paragraph to inherit from, extract its marks
          if (paragraphToInheritFrom) {
            // Try to extract marks from the paragraph's first text node
            const firstChild = paragraphToInheritFrom.get(0);
            
            if (firstChild instanceof Y.XmlText && firstChild.length > 0) {
              // Get formatting from the first character using toDelta()
              const delta = firstChild.toDelta();
              const attrs = (delta && delta.length > 0) ? delta[0].attributes : null;
              
              if (attrs) {
                // Inherit fontFamily (only if not already set)
                if (!documentDefaults.fontFamily && attrs.fontFamily && (attrs.fontFamily as any).font) {
                  documentDefaults.fontFamily = (attrs.fontFamily as any).font;
                }
                
                // Inherit fontSize (only if not already set)
                if (!documentDefaults.fontSize && attrs.fontSize && (attrs.fontSize as any).size) {
                  documentDefaults.fontSize = (attrs.fontSize as any).size;
                }
                
                // Inherit textColor (only if not already set)
                if (!documentDefaults.textColor && attrs.textColor && (attrs.textColor as any).color) {
                  documentDefaults.textColor = (attrs.textColor as any).color;
                }
              }
            }
          }
        } catch (error) {
          // Failed to inherit from previous paragraph (silent fallback)
        }
      }
      
      // Log what defaults were found
      if (documentDefaults.fontFamily || documentDefaults.fontSize || documentDefaults.textColor) {
        log.log('[AI_TOOL_LOG] [Insert] Document defaults detected:', {
          fontFamily: documentDefaults.fontFamily,
          fontSize: documentDefaults.fontSize,
          textColor: documentDefaults.textColor,
          source: docStyleMap ? 'doc_style' : (inheritedStyleId ? 'style_registry' : 'none')
        });
      }
    } catch {}
    
    // Create all elements with change tracking
    // Note: Some items may return null (e.g., empty math nodes), we'll filter those out later
    const elementsWithPossibleNulls = this.content.map(item => {
      // ✅ NEW: Map LLM-friendly heading types to semantic heading + level
      const processedItem = this.mapHeadingTypes(item);
      
      if ((processedItem.type === 'ordered_list' || processedItem.type === 'bullet_list' || processedItem.type === 'list') && processedItem.content) {
        // ✅ FIX: Handle nested list structure
        log.log('[AI_TOOL_LOG] [Insert] Creating nested list structure:', {
          listType: processedItem.type,
          itemCount: processedItem.content.length
        });
        
        // Create the list container element
        const listElement = DocumentUtils.createElement(processedItem.type, '', {
          ...processedItem.metadata,
          docId: context.docId, // Pass docId for proper prefixed node generation
          change_status: suggestionMode ? 'new' : 'none',
          revision_id: suggestionMode ? this.revisionId : undefined
        });
        
        // Create and add list item children
        let listItemIndex = 0;
        for (const listItem of processedItem.content) {
          if (listItem.type === 'list_item' && listItem.content) {
            // Create list item element
            const listItemElement = DocumentUtils.createElement('list_item', '', {
              docId: context.docId, // Pass docId for proper prefixed node generation
              change_status: suggestionMode ? 'new' : 'none',
              revision_id: suggestionMode ? this.revisionId : undefined
            });
            
            // Create paragraph content for the list item
            let childIndex = 0;  // Renamed for clarity
            
            // 🔍 DEBUG: Log what we're processing
            log.log('🔍 [LIST_ITEM_PROCESSING] Processing list item content:', {
              contentCount: listItem.content.length,
              contentTypes: listItem.content.map((c: any) => c.type),
              hasNestedLists: listItem.content.some((c: any) => c.type === 'bullet_list' || c.type === 'ordered_list')
            });
            
            for (const childItem of listItem.content) {  // Renamed from 'paragraph'
              log.log('🔍 [LIST_ITEM_LOOP] Processing content item:', {
                type: childItem.type,
                willProcess: childItem.type === 'paragraph' || childItem.type === 'bullet_list' || childItem.type === 'ordered_list'
              });
              
              if (childItem.type === 'paragraph' && (childItem as any).text) {
                // ✅ FIX: Use createElementWithFormatting to preserve bold/italic/links in list items
                const paragraphElement = DocumentUtils.createElementWithFormatting(
                  'paragraph',
                  (childItem as any).text,
                  (childItem as any).formatting || [],  // ← Preserve formatting!
                  {
                    docId: context.docId,
                    change_status: suggestionMode ? 'new' : 'none',
                    revision_id: suggestionMode ? this.revisionId : undefined,
                    attrs: withInheritedStyle((childItem as any).attributes || (childItem as any).attrs, 'paragraph')
                  }
                );
                listItemElement.insert(childIndex, [paragraphElement]);
                childIndex++;
              } else if (childItem.type === 'bullet_list' || childItem.type === 'ordered_list') {
                // ✅ FIX: Recursively handle nested lists
                log.log('🔍 [LIST_ITEM_LOOP] Recursively creating nested list:', {
                  nestedListType: childItem.type,
                  nestedItemCount: (childItem as any).content?.length
                });
                const nestedListElement = this.createNestedListRecursive(childItem as any, context, suggestionMode, this.revisionId, 1);
                listItemElement.insert(childIndex, [nestedListElement]);
                childIndex++;
              }
            }
            
            listElement.insert(listItemIndex, [listItemElement]);
            listItemIndex++;
          }
        }
        
        return listElement;
      } else if (processedItem.type === 'table' && processedItem.content) {
        // ✅ FIX: Handle nested table structure
        log.log('[AI_TOOL_LOG] [Insert] Creating nested table structure:', {
          tableType: processedItem.type,
          rowCount: processedItem.content.length
        });
        
        // Create the table container element
        const tableElement = DocumentUtils.createElement('table', '', {
          ...processedItem.metadata,
          docId: context.docId, // Pass docId for proper prefixed node generation
          change_status: suggestionMode ? 'new' : 'none',
          revision_id: suggestionMode ? this.revisionId : undefined
        });
        
        // Create and add table row children
        let rowIndex = 0;
        for (const tableRow of processedItem.content) {
          if (tableRow.type === 'table_row' && tableRow.content) {
            // Create table row element
            const tableRowElement = DocumentUtils.createElement('table_row', '', {
              docId: context.docId, // Pass docId for proper prefixed node generation
              change_status: suggestionMode ? 'new' : 'none',
              revision_id: suggestionMode ? this.revisionId : undefined
            });
            
            // Create table cell content for the row
            let cellIndex = 0;
            for (const tableCell of tableRow.content) {
              if ((tableCell.type === 'table_cell' || tableCell.type === 'table_header') && tableCell.content) {
                const tableCellElement = DocumentUtils.createElement(tableCell.type, '', {
                  docId: context.docId, // Pass docId for proper prefixed node generation
                  change_status: suggestionMode ? 'new' : 'none',
                  revision_id: suggestionMode ? this.revisionId : undefined
                });
                
                // Create paragraph content for the cell
                let paragraphIndex = 0;
                for (const paragraph of tableCell.content) {
                  if (paragraph.type === 'paragraph') {
                    // ✅ FIX: Use createElementWithFormatting to preserve bold/italic/links in table cells
                    const paragraphElement = DocumentUtils.createElementWithFormatting(
                      'paragraph',
                      paragraph.text || '',
                      paragraph.formatting || [],  // ← Apply inline formatting!
                      {
                        docId: context.docId,
                        change_status: suggestionMode ? 'new' : 'none',
                        revision_id: suggestionMode ? this.revisionId : undefined,
                        attrs: paragraph.attributes || paragraph.attrs || {}
                      }
                    );
                    tableCellElement.insert(paragraphIndex, [paragraphElement]);
                    paragraphIndex++;
                  }
                }
                
                tableRowElement.insert(cellIndex, [tableCellElement]);
                cellIndex++;
              }
            }
            
            tableElement.insert(rowIndex, [tableRowElement]);
            rowIndex++;
          }
        }
        
        return tableElement;
      } else if (processedItem.type === 'blockquote' && processedItem.content) {
        // 🚨 CRITICAL FIX: Handle blockquote structure
        log.log('[AI_TOOL_LOG] [Insert] Creating blockquote structure:', {
          nestedContentCount: processedItem.content.length
        });
        
        // Create the blockquote container element
        const blockquoteElement = DocumentUtils.createElement('blockquote', '', {
          ...processedItem.metadata,
          docId: context.docId,
          change_status: suggestionMode ? 'new' : 'none',
          revision_id: suggestionMode ? this.revisionId : undefined
        });
        
        // Create and add nested content - handle multiple element types
        for (let i = 0; i < processedItem.content.length; i++) {
          const nestedItem = processedItem.content[i];
          
          // Support headings, lists, and paragraphs in blockquotes
          if (nestedItem.type === 'heading' && nestedItem.text) {
            const headingElement = DocumentUtils.createElement('heading', nestedItem.text, {
              docId: context.docId,
              change_status: suggestionMode ? 'new' : 'none',
              revision_id: suggestionMode ? this.revisionId : undefined,
              attrs: {
                ...withInheritedStyle((nestedItem as any).attributes || (nestedItem as any).attrs, 'heading'),
                level: nestedItem.attrs?.level || 1
              }
            });
            blockquoteElement.insert(i, [headingElement]);
          } else if ((nestedItem.type === 'bullet_list' || nestedItem.type === 'ordered_list') && nestedItem.content) {
            // Nested list in blockquote - recursively create
            const listElement = DocumentUtils.createElement(nestedItem.type, '', {
              docId: context.docId,
              change_status: suggestionMode ? 'new' : 'none',
              revision_id: suggestionMode ? this.revisionId : undefined
            });
            
            for (const listItem of nestedItem.content) {
              if (listItem.type === 'list_item' && listItem.content) {
                const listItemElement = DocumentUtils.createElement('list_item', '', {
                  docId: context.docId,
                  change_status: suggestionMode ? 'new' : 'none',
                  revision_id: suggestionMode ? this.revisionId : undefined
                });
                
                let paraIndex = 0;
                for (const para of listItem.content) {
                  if (para.type === 'paragraph' && para.text) {
                    // ✅ FIX: Use createElementWithFormatting to preserve bold/italic/links in blockquote lists
                    const paragraphElement = DocumentUtils.createElementWithFormatting(
                      'paragraph',
                      para.text,
                      (para as any).formatting || [],  // ← Preserve formatting!
                      {
                        docId: context.docId,
                        change_status: suggestionMode ? 'new' : 'none',
                        revision_id: suggestionMode ? this.revisionId : undefined,
                        attrs: withInheritedStyle((para as any).attributes || (para as any).attrs, 'paragraph')
                      }
                    );
                    listItemElement.insert(paraIndex, [paragraphElement]);
                    paraIndex++;
                  }
                }
                
                listElement.insert(listElement.length, [listItemElement]);
              }
            }
            
            blockquoteElement.insert(i, [listElement]);
          } else if (nestedItem.type === 'paragraph' && nestedItem.text) {
            // ✅ FIX: Use createElementWithFormatting to preserve bold/italic/links in blockquote paragraphs
            const paragraphElement = DocumentUtils.createElementWithFormatting(
              'paragraph',
              nestedItem.text,
              (nestedItem as any).formatting || [],  // ← Preserve formatting!
              {
                docId: context.docId,
                change_status: suggestionMode ? 'new' : 'none',
                revision_id: suggestionMode ? this.revisionId : undefined,
                attrs: withInheritedStyle((nestedItem as any).attributes || (nestedItem as any).attrs, 'paragraph')
              }
            );
            blockquoteElement.insert(i, [paragraphElement]);
          }
        }
        
        return blockquoteElement;
      } else if (processedItem.type === 'math_inline' || processedItem.type === 'math_display') {
        // Handle math nodes - these are atomic (no text content)
        // 🔧 DEFENSIVE: Extract and validate latex
        const latex = String((processedItem as any).latex || '');
        
        
        log.log('[AI_TOOL_LOG] [Insert] Creating math node:', {
          type: processedItem.type,
          latex: latex.substring(0, 50),
          latexLength: latex.length,
          displayMode: processedItem.type === 'math_display'
        });
        
        // 🔧 DEFENSIVE: Skip if no latex content
        if (!latex || latex.trim().length === 0) {
          log.error('[AI_TOOL_LOG] [Insert] Empty latex in math node, skipping');
          return null;
        }
        
        // 🔧 SECURITY: Validate latex length
        if (latex.length > 10000) {
          log.error('[AI_TOOL_LOG] [Insert] LaTeX too long (>10000 chars), truncating');
          // Truncate instead of failing completely
          const truncatedLatex = latex.substring(0, 10000);
          const mathElement = DocumentUtils.createElement(processedItem.type, '', {
            docId: context.docId,
            change_status: suggestionMode ? 'new' : 'none',
            revision_id: suggestionMode ? this.revisionId : undefined,
            attrs: {
              latex: truncatedLatex
            }
          });
          return mathElement;
        }
        
        const mathElement = DocumentUtils.createElement(processedItem.type, '', {
          docId: context.docId,
          change_status: suggestionMode ? 'new' : 'none',
          revision_id: suggestionMode ? this.revisionId : undefined,
          attrs: {
            latex: latex
          }
        });
        
        
        return mathElement;
      } else {
        // Handle regular content with text and formatting
        const inheritedAttrs = withInheritedStyle((processedItem as any).attributes || processedItem.metadata?.attrs, processedItem.type as any);
        
        // Merge item attrs (like heading level) with inherited style attrs
        // The markdownToContentArray function sets attrs.level for headings, which was being lost
        const mergedAttrs = {
          ...inheritedAttrs,
          ...((processedItem as any).attrs || {}) // Merge in attrs from markdownToContentArray
        };
        
        // ✨ SMART INHERITANCE: Apply document defaults to unstyled text
        // Strategy: Apply defaults to ENTIRE text. If AI explicitly styled portions,
        // those explicit styles will override defaults during Y.js format application
        // (later .format() calls on same range override earlier ones)
        let enhancedFormatting = processedItem.formatting || [];
        
        // ✅ CRITICAL FIX: Apply inherited defaults even if paragraph has a style_id
        // The 'normal' style is just a base style - inheritance should still work
        // Priority: Inherited defaults > Style defaults > System defaults
        if (processedItem.type === 'paragraph' || processedItem.type === 'heading') {
          const textLength = (processedItem.text || '').length;
          
          if (textLength > 0 && (documentDefaults.fontFamily || documentDefaults.fontSize || documentDefaults.textColor)) {
            // Build document defaults to apply to entire text
            const defaultMarks: Record<string, any> = {};
            
            if (documentDefaults.fontFamily) {
              // ✅ CRITICAL FIX: Use correct mark format {font: string}, not just string
              defaultMarks.fontFamily = { font: documentDefaults.fontFamily };
            }
            
            if (documentDefaults.fontSize) {
              defaultMarks.fontSize = { size: documentDefaults.fontSize };
            }
            
            if (documentDefaults.textColor) {
              // ✅ NEW: Apply default text color to all content
              defaultMarks.textColor = { color: documentDefaults.textColor };
            }
            
            // ✅ KEY INSIGHT: Apply defaults FIRST, then explicit styles
            // This way, explicit styles override defaults on their specific ranges
            if (Object.keys(defaultMarks).length > 0) {
              const defaultMark = { start: 0, end: textLength, marks: defaultMarks };
              // Prepend defaults so explicit formatting comes after (and overrides)
              enhancedFormatting = [defaultMark, ...enhancedFormatting];
              
              log.log('[AI_TOOL_LOG] [Insert] Applied document defaults to entire text (explicit styles will override):', {
                appliedDefaults: Object.keys(defaultMarks),
                defaultValues: defaultMarks,
                explicitFormattingRanges: enhancedFormatting.length - 1,
                textLength: textLength
              });
            }
          }
        }
        
        return DocumentUtils.createElementWithFormatting(
          processedItem.type, 
          processedItem.text || '', 
          enhancedFormatting,
          {
            ...processedItem.metadata,
            attrs: mergedAttrs,
            docId: context.docId,
            change_status: suggestionMode ? 'new' : 'none',
            revision_id: suggestionMode ? this.revisionId : undefined
          }
        );
      }
    });
    
    // 🔧 CRITICAL FIX: Filter out null elements (e.g., from empty math nodes)
    // TypeScript requires the array to have no null values for Y.js insertion
    const elements = elementsWithPossibleNulls.filter((el): el is Y.XmlElement => el !== null);
    
    // Log if any elements were filtered out
    if (elementsWithPossibleNulls.length !== elements.length) {
      const filteredCount = elementsWithPossibleNulls.length - elements.length;
      log.warn(`[AI_TOOL_LOG] [Insert] Filtered out ${filteredCount} null elements (likely empty math nodes)`);
    }
    
    // Determine insertion point
    let insertIndex: number;
    
    if ('after' in this.location) {
      const found = DocumentUtils.findNodeById(yxml, this.location.after);
      if (!found) {
        log.error('[AI_TOOL_LOG] [Insert] Node not found for after location:', this.location.after);
        return false;
      }
      insertIndex = found.index + 1;
    } else if ('before' in this.location) {
      const found = DocumentUtils.findNodeById(yxml, this.location.before);
      if (!found) {
        log.error('[AI_TOOL_LOG] [Insert] Node not found for before location:', this.location.before);
        return false;
      }
      insertIndex = found.index;
    } else if ('at' in this.location) {
      insertIndex = this.location.at === 'start' ? 0 : yxml.length;
    } else {
      insertIndex = Math.max(0, Math.min(this.location.index, yxml.length));
    }
    
    // 📊 PRE-INSERTION Y.JS ANALYSIS
    const preYjsState = {
      yxmlLengthBefore: yxml.length,
      insertIndex,
      elementsToInsert: elements.length,
      docId: context.docId,
      revisionId: this.revisionId,
      suggestionMode,
      timestamp: new Date().toISOString()
    };
    
    // 🔍 CRITICAL: Analyze Y.js structure before insertion to understand page hierarchy
    log.log('🔍 [Y.JS_STRUCTURE_DEBUG] === Y.JS STRUCTURE BEFORE AI INSERTION ===', {
      timestamp: new Date().toISOString(),
      yxmlLength: yxml.length,
      insertIndex,
      yxmlElements: Array.from({length: yxml.length}, (_, i) => {
        const element = yxml.get(i);
        if (element instanceof Y.XmlElement) {
          return {
            index: i,
            nodeName: element.nodeName,
            type: element.getAttribute('type'),
            id: element.getAttribute('id'),
            childCount: element.length,
            isPageNode: element.nodeName === 'page',
            // 🚨 CRITICAL FIX: DO NOT use element.toString() - causes contamination!
            textPreview: (() => {
              try {
                const anyElement = element as any;
                if (anyElement._content && typeof anyElement._content === 'string') {
                  return anyElement._content.substring(0, 50);
                } else if (anyElement.text && typeof anyElement.text === 'string') {
                  return anyElement.text.substring(0, 50);
                }
                return '[text not safely accessible]';
              } catch {
                return '[text extraction failed]';
              }
            })()
          };
        }
        return { index: i, type: 'text', isPageNode: false };
      }),
      willInsertAt: `index ${insertIndex}`,
      insertionContext: this.location,
      problemSuspected: 'Content may be inserted at document level instead of inside page'
    });
    
    log.log(`🔧 [YJS_INSERT] Y.js insertion at index ${insertIndex} - ${elements.length} elements`);
    
    doc.transact(() => {
      // Clear node cache to prevent stale reads (matches REPLACE behavior)
      yDocumentService.clearNodeCache(doc?.guid);
      
      // Determine correct insertion parent and index
      const { parentContainer, actualIndex } = this.resolveInsertionTarget(yxml, insertIndex, this.location);
      
      parentContainer.insert(actualIndex, elements);
      
      // ✅ CRITICAL DEBUG: Verify insertion succeeded
      const lengthAfterInsert = parentContainer.length;
      log.log('🔍 [INSERT-VERIFY] Insertion complete:', {
        insertedCount: elements.length,
        insertedAt: actualIndex,
        yxmlLengthBefore: insertIndex, // This was the original length
        yxmlLengthAfter: lengthAfterInsert,
        expectedLength: insertIndex + elements.length,
        matches: lengthAfterInsert === (insertIndex + elements.length)
      });
      
      // 🚨 CRITICAL: Extract node IDs safely while transaction is active
      // getAttribute('id') is safe and just reads string values
      log.log('🔍 [SAFE_ID_EXTRACTION] Insert - About to extract IDs from', elements.length, 'elements');
      
      this.insertedNodeIds = elements
        .map((el, idx) => {
          try {
            // 🔧 DEFENSIVE: Double-check el is not null (TypeScript safety)
            if (!el) {
              console.warn(`⚠️ [SAFE_ID_EXTRACTION] Insert element ${idx} is null`);
              return undefined;
            }
            const id = el.getAttribute?.('id');
            if (!id) {
              console.warn(`⚠️ [SAFE_ID_EXTRACTION] Insert element ${idx} has no ID`);
            }
            return id;
          } catch (err) {
            console.error(`❌ [SAFE_ID_EXTRACTION] Insert error extracting ID from element ${idx}:`, err);
            return undefined;
          }
        })
        .filter(Boolean) as string[];
      
      log.log('✅ [SAFE_ID_EXTRACTION] Insert - Extracted', this.insertedNodeIds.length, 'IDs:', this.insertedNodeIds);
      
      log.log(`✅ [YJS_INSERT] Inserted ${elements.length} elements into ${parentContainer === yxml ? 'fragment' : 'page'} at index ${actualIndex}`);
    }, yDocHighLevelApiOrigin);
    
    log.log(`✅ [YJS_INSERT] Y.js insertion complete - syncing to ProseMirror...`);
    
    return true;
  }
  
  /**
   * Resolve where to insert content based on location and document structure
   * Makes insertion page-aware for hierarchical documents
   */
  private resolveInsertionTarget(
    yxml: Y.XmlFragment, 
    insertIndex: number, 
    location: Insert['location']
  ): { parentContainer: Y.XmlFragment | Y.XmlElement; actualIndex: number } {
    
    // For specific node-based insertion (after/before a target node)
    if ('after' in location || 'before' in location) {
      const targetNodeId = 'after' in location ? location.after : location.before;
      const found = DocumentUtils.findNodeById(yxml, targetNodeId);
      
      if (found) {
        // Check if target is a page node
        if (found.element instanceof Y.XmlElement && found.element.nodeName === 'page') {
          return { parentContainer: found.element, actualIndex: 0 };
        }
        
        // Check if target is inside a page
        if (found.parent instanceof Y.XmlElement && found.parent.nodeName === 'page') {
          const indexInPage = found.index;
          const adjustedIndex = 'after' in location ? indexInPage + 1 : indexInPage;
          return { parentContainer: found.parent, actualIndex: adjustedIndex };
        }
        
        // Fallback: insert at fragment level
        return { parentContainer: yxml, actualIndex: insertIndex };
      }
    }
    
    // For 'at: start/end' or index-based insertion, check if we should insert in first page
    if (yxml.length > 0) {
      const firstElement = yxml.get(0);
      if (firstElement instanceof Y.XmlElement && firstElement.nodeName === 'page') {
        // If inserting at start, put at beginning of first page
        if ('at' in location && location.at === 'start') {
          return { parentContainer: firstElement, actualIndex: 0 };
        }
        // If inserting at end, put at end of last page
        if ('at' in location && location.at === 'end') {
          const lastElement = yxml.get(yxml.length - 1);
          if (lastElement instanceof Y.XmlElement && lastElement.nodeName === 'page') {
            return { parentContainer: lastElement, actualIndex: lastElement.length };
          }
        }
      }
    }
    
    // Default: insert at fragment level (for backward compatibility)
    return { parentContainer: yxml, actualIndex: insertIndex };
  }
  
  getInsertedNodeIds(): string[] {
    const ids = this.insertedNodeIds || [];
    log.log('📤 [SAFE_ID_EXTRACTION] Insert.getInsertedNodeIds() returning', ids.length, 'IDs:', ids);
    return ids;
  }
  
  getInsertedNodes(context: CommandContext): ReadResult['nodes'] {
    // We need to find the nodes that were just inserted.
    // The revisionId is attached to all newly created nodes.
    const allNodes = Read.all(context).nodes;
    return allNodes.filter(node => node.attrs?.revision_id === this.revisionId);
  }
  
  /**
   * 🆕 Helper method to recursively create nested list structures
   * This is needed because list items can contain nested lists, which can contain more nested lists, etc.
   */
  private createNestedListRecursive(
    listContentItem: any,
    context: CommandContext,
    suggestionMode: boolean,
    revisionId: string,
    depth: number = 0
  ): Y.XmlElement {
    log.log(`${'  '.repeat(depth)}🔍 [RECURSIVE_LIST] Creating list at depth ${depth}:`, {
      listType: listContentItem.type,
      itemCount: listContentItem.content?.length || 0
    });
    
    const listElement = DocumentUtils.createElement(listContentItem.type as NodeType, '', {
      ...listContentItem.metadata,
      docId: context.docId,
      change_status: suggestionMode ? 'new' : 'none',
      revision_id: suggestionMode ? revisionId : undefined
    });
  
    let listItemIndex = 0;
    for (const listItem of listContentItem.content || []) {
      log.log(`${'  '.repeat(depth)}🔍 [RECURSIVE_LIST] Processing list item at depth ${depth}:`, {
        listItemType: listItem.type,
        hasContent: !!listItem.content,
        contentCount: listItem.content?.length || 0,
        contentTypes: listItem.content?.map((c: any) => c.type)
      });
      
      if (listItem.type === 'list_item' && listItem.content) {
        const listItemElement = DocumentUtils.createElement('list_item', '', {
          docId: context.docId,
          change_status: suggestionMode ? 'new' : 'none',
          revision_id: suggestionMode ? revisionId : undefined
        });
  
        let childIndex = 0;
        for (const childItem of listItem.content) {
          log.log(`${'  '.repeat(depth)}  🔍 [RECURSIVE_LIST] Processing child at depth ${depth}:`, {
            childType: childItem.type,
            childText: (childItem as any).text?.substring(0, 30)
          });
          
          if (childItem.type === 'paragraph' && childItem.text) {
            const paragraphElement = DocumentUtils.createElementWithFormatting(
              'paragraph',
              childItem.text,
              childItem.formatting || [],
              {
                docId: context.docId,
                change_status: suggestionMode ? 'new' : 'none',
                revision_id: suggestionMode ? revisionId : undefined,
                attrs: childItem.attributes || childItem.attrs || {}
              }
            );
            listItemElement.insert(childIndex, [paragraphElement]);
            childIndex++;
            log.log(`${'  '.repeat(depth)}  ✅ [RECURSIVE_LIST] Paragraph inserted at depth ${depth}`);
          } else if (childItem.type === 'bullet_list' || childItem.type === 'ordered_list') {
            // Recursively handle deeper nesting
            log.log(`${'  '.repeat(depth)}  🔁 [RECURSIVE_LIST] RECURSING to depth ${depth + 1} for ${childItem.type}`);
            const nestedListElement = this.createNestedListRecursive(childItem, context, suggestionMode, revisionId, depth + 1);
            log.log(`${'  '.repeat(depth)}  🔁 [RECURSIVE_LIST] RETURNED from depth ${depth + 1}, inserting nested list`);
            listItemElement.insert(childIndex, [nestedListElement]);
            childIndex++;
            log.log(`${'  '.repeat(depth)}  ✅ [RECURSIVE_LIST] Nested list inserted at depth ${depth}`);
          }
        }
        listElement.insert(listItemIndex, [listItemElement]);
        listItemIndex++;
      }
    }
    
    log.log(`${'  '.repeat(depth)}✅ [RECURSIVE_LIST] Completed list at depth ${depth}:`, {
      listType: listContentItem.type,
      totalItems: listItemIndex
    });
    
    return listElement;
  }

  describe(): string {
    const contentDesc = this.content.length === 1 
      ? `a ${this.content[0].type}`
      : `${this.content.length} nodes`;
      
    if ('after' in this.location) {
      return `Insert ${contentDesc} after node ${this.location.after}`;
    } else if ('before' in this.location) {
      return `Insert ${contentDesc} before node ${this.location.before}`;
    } else if ('at' in this.location) {
      return `Insert ${contentDesc} at ${this.location.at}`;
    } else {
      return `Insert ${contentDesc} at index ${this.location.index}`;
    }
  }
}

/**
 * Remove content from the document by reference
 */
export class Remove implements DocumentCommand {
  private revisionId: string;
  private batchDescription: string;
  
  constructor(
    private target:
      | { id: NodeId }                    // Remove a specific node
      | { ids: NodeId[] }                 // Remove multiple specific nodes
      | { from: NodeId; to: NodeId }      // Remove a range (inclusive)
      | { all: true },                    // Clear the document
    options: {
      revisionId?: string;
      batchDescription?: string;
      suggestionMode?: boolean;
    } = {}
  ) {
    this.revisionId = options.revisionId || `rev_${nanoid(10)}`;
    this.batchDescription = options.batchDescription || 'Remove operation';
    log.log('[AI_TOOL_LOG] [Remove] Command created:', {
      target,
      revisionId: this.revisionId,
      batchDescription: this.batchDescription,
      suggestionMode: options.suggestionMode
    });
  }
  
  validate(context: CommandContext): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if ('id' in this.target) {
      const validation = DocumentUtils.validateNodeId(this.target.id);
      if (!validation.isValid) {
        errors.push(`Invalid node ID: ${validation.error}`);
      }
    } else if ('ids' in this.target) {
      if (!this.target.ids || this.target.ids.length === 0) {
        errors.push('Node IDs array cannot be empty');
      }
      for (const id of this.target.ids) {
        const validation = DocumentUtils.validateNodeId(id);
        if (!validation.isValid) {
          errors.push(`Invalid node ID: ${validation.error}`);
        }
      }
    } else if ('from' in this.target) {
      const fromValidation = DocumentUtils.validateNodeId(this.target.from);
      const toValidation = DocumentUtils.validateNodeId(this.target.to);
      if (!fromValidation.isValid) {
        errors.push(`Invalid 'from' node ID: ${fromValidation.error}`);
      }
      if (!toValidation.isValid) {
        errors.push(`Invalid 'to' node ID: ${toValidation.error}`);
      }
    }
    
    return { isValid: errors.length === 0, errors };
  }
  
  execute(context: CommandContext): boolean {
    log.log('[AI_TOOL_LOG] [Remove] Executing command:', {
      target: this.target,
      revisionId: this.revisionId
    });
    
    // Validate before execution
    const validation = this.validate(context);
    if (!validation.isValid) {
      log.error('[AI_TOOL_LOG] [Remove] Validation failed:', validation.errors);
      return false;
    }
    
    const { yxml, doc } = context;
    const suggestionMode = context.metadata?.suggestionMode !== false; // Default to true
    
    // Collect nodes to remove
    const toRemove: NodeLocation[] = [];
    
    if ('id' in this.target) {
      const found = DocumentUtils.findNodeById(yxml, this.target.id);
      if (found) toRemove.push(found);
    } else if ('ids' in this.target) {
      for (const id of this.target.ids) {
        const found = DocumentUtils.findNodeById(yxml, id);
        if (found) toRemove.push(found);
      }
    } else if ('from' in this.target) {
      const fromNode = DocumentUtils.findNodeById(yxml, this.target.from);
      const toNode = DocumentUtils.findNodeById(yxml, this.target.to);
      
      if (fromNode && toNode && fromNode.parent === toNode.parent) {
        const parent = fromNode.parent;
        const startIdx = Math.min(fromNode.index, toNode.index);
        const endIdx = Math.max(fromNode.index, toNode.index);
        
        for (let i = startIdx; i <= endIdx; i++) {
          const element = parent.get(i);
          if (element instanceof Y.XmlElement) {
            toRemove.push({ element, index: i, parent });
          }
        }
      }
    } else if (this.target.all) {
      // Mark all top-level nodes for removal
      for (let i = 0; i < yxml.length; i++) {
        const element = yxml.get(i);
        if (element instanceof Y.XmlElement) {
          toRemove.push({ element, index: i, parent: yxml });
        }
      }
    }
    
    if (toRemove.length === 0) {
      log.warn('[AI_TOOL_LOG] [Remove] No valid nodes found to remove from the provided list');
      // If some nodes were requested but none were found, this is an error.
      const requestedIds = 'id' in this.target ? [this.target.id] : 'ids' in this.target ? this.target.ids : [];
      if (requestedIds.length > 0) {
        return false;
      }
      // If no nodes were requested (e.g., empty ids array), this is not an error.
      return true;
    }
    
    // Log if some nodes were not found but we are proceeding with the ones that were
    if ('ids' in this.target && toRemove.length < this.target.ids.length) {
      const foundIds = new Set(toRemove.map(n => n.element.getAttribute('id')));
      const missingIds = this.target.ids.filter(id => !foundIds.has(id));
      log.warn('[AI_TOOL_LOG] [Remove] Some nodes not found, but proceeding with valid ones:', { missingIds });
    }
    
    // Perform removal in a transaction
    doc.transact(() => {
      toRemove.forEach(({ element, parent, index }) => {
        const nodeId = element.getAttribute('id') || `unknown_${Date.now()}`;
        const patchId = new ObjectId().toString();
        
        // Store content before removal for potential recovery
        const rawAttrs = element.getAttributes ? element.getAttributes() : {};
        const { change_status, revision_id, diff_type, ...cleanAttrs } = (rawAttrs || {}) as Record<string, any>;
        const contentBefore = {
          type: element.nodeName,
          text: DocumentUtils.getTextContent(element),
          attrs: cleanAttrs
        };
        
        if (suggestionMode) {
          // Special case: if this node is a pending insertion (change_status === 'new'),
          // treat deletion as a hard removal rather than creating a nested suggestion.
          const isPendingInsertion = String(element.getAttribute('change_status') || '') === 'new';
          if (isPendingInsertion) {
            try {
              parent.delete(index, 1);
            } catch (e) {
              log.warn('[AI_TOOL_LOG] [Remove] Failed hard-delete of pending insertion, falling back to marking old', { nodeId });
              PatchLogger.applyDualMarking(element, 'delete', this.revisionId, suggestionMode);
            }
          } else {
            // For existing content, mark as deleted (old)
            PatchLogger.applyDualMarking(element, 'delete', this.revisionId, suggestionMode);
          }
        } else {
          // Direct removal handled below when suggestionMode is false
        }
        
        // Log the patch
        PatchLogger.logPatch(context, {
          patchId,
          nodeId,
          op: 'delete',
          contentBefore,
          revisionId: this.revisionId,
          // If we hard-deleted a pending insertion, mark as accepted to avoid dangling pending entries
          status: suggestionMode ? (String(element.getAttribute('change_status') || '') === 'new' ? 'accepted' : 'pending') : 'accepted',
          batchDescription: this.batchDescription,
          authorType: 'agent'
        });
      });
      
      if (!suggestionMode) {
        // Direct removal - sort by index descending to avoid position shifts
        toRemove
          .sort((a, b) => b.index - a.index)
          .forEach(({ parent, index }) => {
            parent.delete(index, 1);
          });
        log.log('[AI_TOOL_LOG] [Remove] Physically removed nodes:', toRemove.length);
      } else {
        log.log('[AI_TOOL_LOG] [Remove] Marked nodes as old (suggestion mode):', toRemove.length);
      }
    }, yDocHighLevelApiOrigin);
    
    log.log('[AI_TOOL_LOG] [Remove] Command executed successfully:', {
      nodesProcessed: toRemove.length,
      suggestionMode,
      revisionId: this.revisionId
    });
    
    return true;
  }
  
  describe(): string {
    if ('id' in this.target) {
      return `Remove node ${this.target.id}`;
    } else if ('ids' in this.target) {
      return `Remove ${this.target.ids.length} nodes`;
    } else if ('from' in this.target) {
      return `Remove nodes from ${this.target.from} to ${this.target.to}`;
    } else {
      return `Clear document`;
    }
  }
}

/**
 * Replace a set/range of nodes with new content atomically
 */
export class Replace implements DocumentCommand {
  private revisionId: string;
  private batchDescription: string;
  private suggestionMode: boolean;
  private insertedElements: Y.XmlElement[] = [];
  private insertedNodeIds: string[] = [];

  constructor(
    private target:
      | { ids: NodeId[] }                 // Replace multiple specific nodes
      | { from: NodeId; to: NodeId },     // Replace a range (inclusive)
    private content: Array<{
      type: NodeType;
      text?: string;
      content?: any[];
      src?: string;
      attrs?: any;
      metadata?: Partial<NodeMetadata>;
      formatting?: Array<{ start: number; end: number; marks: Record<string, any> }>;
    }>,
    options: {
      revisionId?: string;
      batchDescription?: string;
      suggestionMode?: boolean;
    } = {}
  ) {
    this.revisionId = options.revisionId || `rev_${nanoid(10)}`;
    this.batchDescription = options.batchDescription || 'Replace operation';
    this.suggestionMode = options.suggestionMode !== false; // default true
    log.log('[AI_TOOL_LOG] [Replace] Command created:', {
      target,
      contentCount: content.length,
      revisionId: this.revisionId,
      suggestionMode: this.suggestionMode
    });
  }

  validate(context: CommandContext): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const { errors: contentErrors } = validateAndSanitizeDocContent(this.content);
    errors.push(...contentErrors);

    if ('ids' in this.target) {
      if (!this.target.ids || this.target.ids.length === 0) errors.push('ids cannot be empty');
      for (const id of this.target.ids) {
        const v = DocumentUtils.validateNodeId(id);
        if (!v.isValid) errors.push(`Invalid node ID: ${v.error}`);
      }
    } else if ('from' in this.target) {
      const vFrom = DocumentUtils.validateNodeId(this.target.from);
      const vTo = DocumentUtils.validateNodeId(this.target.to);
      if (!vFrom.isValid) errors.push(`Invalid 'from' node ID: ${vFrom.error}`);
      if (!vTo.isValid) errors.push(`Invalid 'to' node ID: ${vTo.error}`);
    }
    return { isValid: errors.length === 0, errors };
  }

  private buildElements(context: CommandContext): Y.XmlElement[] {
    // Helper to extract plain text from ProseMirror-like nodes
    const extractText = (node: any): string => {
      if (!node) return '';
      if (typeof node.text === 'string') return node.text;
      if (Array.isArray(node.content)) return node.content.map(extractText).join('');
      return '';
    };

    // Reuse Insert logic style for consistent element creation (no-op instance to keep parity)
    new Insert(this.content, { index: 0 }, {
      revisionId: this.revisionId,
      batchDescription: this.batchDescription,
      suggestionMode: this.suggestionMode
    });

    // Duplicate creation logic using DocumentUtils with better content handling
    const elements = this.content.map(item => {
      // Handle lists
      if ((item.type === 'ordered_list' || item.type === 'bullet_list' || (item as any).type === 'list') && item.content) {
        const listElement = DocumentUtils.createElement(item.type as any, '', {
          ...item.metadata,
          docId: context.docId,
          change_status: this.suggestionMode ? 'new' : 'none',
          revision_id: this.suggestionMode ? this.revisionId : undefined
        });
        for (const li of item.content) {
          if (li && li.type === 'list_item' && Array.isArray(li.content)) {
            const liEl = DocumentUtils.createElement('list_item', '', {
              docId: context.docId,
              change_status: this.suggestionMode ? 'new' : 'none',
              revision_id: this.suggestionMode ? this.revisionId : undefined
            });
            let pIndex = 0;
            for (const p of li.content) {
              if (p && p.type === 'paragraph') {
                const pText = typeof p.text === 'string' ? p.text : extractText(p);
                liEl.insert(pIndex, [DocumentUtils.createElement('paragraph', pText, {
                  docId: context.docId,
                  change_status: this.suggestionMode ? 'new' : 'none',
                  revision_id: this.suggestionMode ? this.revisionId : undefined
                })]);
                pIndex++;
              }
            }
            listElement.insert(listElement.length, [liEl]);
          }
        }
        return listElement;
      }

      // Generic block (paragraph, heading, blockquote, etc.)
      const textContent = typeof (item as any).text === 'string' ? (item as any).text : extractText(item);
      const attrs = (item as any).attrs || (item as any).attributes || (item as any).metadata?.attrs;
      return DocumentUtils.createElementWithFormatting(item.type as any, textContent, (item as any).formatting, {
        ...item.metadata,
        attrs,
        docId: context.docId,
        change_status: this.suggestionMode ? 'new' : 'none',
        revision_id: this.suggestionMode ? this.revisionId : undefined
      });
    });
    return elements;
  }

  execute(context: CommandContext): boolean {
    log.log('[AI_TOOL_LOG] [Replace] Executing command');
    const validation = this.validate(context);
    if (!validation.isValid) {
      log.error('[AI_TOOL_LOG] [Replace] Validation failed:', validation.errors);
      return false;
    }

    const { yxml, doc } = context;

    // Collect target locations
    const toReplace: NodeLocation[] = [];
    if ('ids' in this.target) {
      for (const id of this.target.ids) {
        const found = DocumentUtils.findNodeById(yxml, id);
        if (found) toReplace.push(found);
      }
    } else {
      const fromNode = DocumentUtils.findNodeById(yxml, this.target.from);
      const toNode = DocumentUtils.findNodeById(yxml, this.target.to);
      if (fromNode && toNode && fromNode.parent === toNode.parent) {
        const parent = fromNode.parent;
        const startIdx = Math.min(fromNode.index, toNode.index);
        const endIdx = Math.max(fromNode.index, toNode.index);
        for (let i = startIdx; i <= endIdx; i++) {
          const el = parent.get(i);
          if (el instanceof Y.XmlElement) toReplace.push({ element: el, index: i, parent });
        }
      }
    }

    if (toReplace.length === 0) {
      log.warn('[AI_TOOL_LOG] [Replace] No valid nodes found to replace');
      return false;
    }

    // Ensure same parent for ids case
    const parentRef = toReplace[0].parent;
    if (!toReplace.every(n => n.parent === parentRef)) {
      log.error('[AI_TOOL_LOG] [Replace] Nodes have different parents; cross-parent replacement not supported');
      return false;
    }

    // Compute min/max across contiguous block ONLY (ignore IDs outside explicit span)
    const indices = toReplace.map(n => n.index).sort((a,b)=>a-b);
    const minIndex = indices[0];
    const maxIndex = indices[indices.length-1];
    const elements = this.buildElements(context);
    const suggestionMode = this.suggestionMode;

    try {
      emitTraceLog({
        traceId: (context.metadata as any)?.traceId,
        type: 'crdt_replace_execute',
        data: {
          targets: toReplace.length,
          contentCount: this.content.length,
          suggestionMode
        }
      });
    } catch {}

    doc.transact(() => {
      if (suggestionMode) {
        // ✅ ENHANCED: Handle replacement of pending insertions (green suggestions)
        // If we're replacing a node that's already marked as 'new', delete it instead of marking as old
        toReplace.forEach(({ element }) => {
          const isPendingInsertion = String(element.getAttribute('change_status') || '') === 'new';
          if (isPendingInsertion) {
            // This is a green suggestion being replaced - delete it entirely
            log.log(`🗑️ [REPLACE_DEBUG] Deleting pending insertion (green suggestion): ${element.getAttribute('id')}`);
            try {
              const parent = element.parent as Y.XmlFragment;
              // Find the index of this element in its parent
              let elementIndex = -1;
              for (let i = 0; i < parent.length; i++) {
                if (parent.get(i) === element) {
                  elementIndex = i;
                  break;
                }
              }
              if (elementIndex !== -1) {
                parent.delete(elementIndex, 1);
              } else {
                console.warn('[REPLACE_DEBUG] Could not find element index in parent, falling back to marking old');
                PatchLogger.applyDualMarking(element, 'delete', this.revisionId, true);
              }
            } catch (e) {
              console.warn('[REPLACE_DEBUG] Failed to delete pending insertion, falling back to marking old:', e);
              PatchLogger.applyDualMarking(element, 'delete', this.revisionId, true);
            }
          } else {
            // This is original content - mark as old (red)
            log.log(`📝 [REPLACE_DEBUG] Marking original content as old: ${element.getAttribute('id')}`);
            PatchLogger.applyDualMarking(element, 'delete', this.revisionId, true);
          }
        });
        // Insert after last old node
        parentRef.insert(maxIndex + 1, elements);
      } else {
        // Physical removal then insert at start index
        toReplace.sort((a, b) => b.index - a.index).forEach(({ parent, index }) => parent.delete(index, 1));
        parentRef.insert(minIndex, elements);
      }

      this.insertedElements = elements;

      // 🚨 CRITICAL: Extract node IDs safely while transaction is active
      // getAttribute('id') is safe and just reads string values
      log.log('🔍 [SAFE_ID_EXTRACTION] Replace - About to extract IDs from', elements.length, 'elements');
      
      this.insertedNodeIds = elements
        .map((el, idx) => {
          try {
            const id = el.getAttribute?.('id');
            if (!id) {
              console.warn(`⚠️ [SAFE_ID_EXTRACTION] Replace element ${idx} has no ID`);
            }
            return id;
          } catch (err) {
            console.error(`❌ [SAFE_ID_EXTRACTION] Replace error extracting ID from element ${idx}:`, err);
            return undefined;
          }
        })
        .filter(Boolean) as string[];
      
      log.log('✅ [SAFE_ID_EXTRACTION] Replace - Extracted', this.insertedNodeIds.length, 'IDs:', this.insertedNodeIds);

      // Log insert patches
      elements.forEach((element, i) => {
        if (element && element.getAttribute) {
          const nodeId = element.getAttribute('id') || `auto_${Date.now()}_${i}`;
      const patchId = `patch_${nanoid(12)}`;
          PatchLogger.logPatch(context, {
            patchId,
            nodeId,
            op: 'insert',
            contentAfter: this.content[i],
            revisionId: this.revisionId,
            status: suggestionMode ? 'pending' : 'accepted',
            batchDescription: this.batchDescription,
            authorType: 'agent'
          });
        }
      });
    }, yDocHighLevelApiOrigin);

    log.log('[AI_TOOL_LOG] [Replace] Command executed successfully:', {
      targets: toReplace.length,
      inserted: elements.length,
      minIndex,
      maxIndex,
      suggestionMode
    });

    return true;
  }

  describe(): string {
    const targetDesc = 'ids' in this.target ? `${this.target.ids.length} nodes` : `range ${this.target.from}..${this.target.to}`;
    return `Replace ${targetDesc} with ${this.content.length} node(s)`;
  }

  getInsertedNodeIds(): string[] {
    const ids = this.insertedNodeIds || [];
    log.log('📤 [SAFE_ID_EXTRACTION] Replace.getInsertedNodeIds() returning', ids.length, 'IDs:', ids);
    return ids;
  }

  getInsertedNodes(context: CommandContext): ReadResult['nodes'] {
    return this.insertedElements.map(element => Read['elementToNode'](element));
  }
}

/**
 * Transform existing content in the document
 */
export class Transform implements DocumentCommand {
  private revisionId: string
  
  // ❌ REMOVED: Redundant decoder - fixing root cause instead
  
  constructor(
    private target: NodeId | NodeId[],
    private transformation: 
      | { text: string | Record<string, string> }  // Replace text content (supports multi-node mapping)
      | { richContent: Array<{ text: string; marks?: Array<{ type: string; attrs?: Record<string, any> }> }> } // Rich text with formatting marks
      | { type: NodeType }                         // Change node type
      | { attrs: Record<string, any> }             // Update attributes
      | { fn: (element: Y.XmlElement) => void },   // Custom transformation
    options: {
      revisionId?: string;
      suggestionMode?: boolean;
    } = {}
  ) {
    this.revisionId = options.revisionId || `rev_${nanoid(12)}`;
    log.log('[AI_TOOL_LOG] [Transform] Command created:', {
      target,
      transformation,
      revisionId: this.revisionId,
      suggestionMode: options.suggestionMode
    });
  }
  
  validate(context: CommandContext): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    const targetIds = Array.isArray(this.target) ? this.target : [this.target];
    for (const id of targetIds) {
      const validation = DocumentUtils.validateNodeId(id);
      if (!validation.isValid) {
        errors.push(`Invalid node ID: ${validation.error}`);
      }
    }
    
    return { isValid: errors.length === 0, errors };
  }
  
  execute(context: CommandContext): boolean {
    log.log('[AI_TOOL_LOG] [Transform] Executing command:', {
      target: this.target,
      transformation: this.transformation,
      revisionId: this.revisionId
    });
    
    // Validate before execution
    const validation = this.validate(context);
    if (!validation.isValid) {
      log.error('[AI_TOOL_LOG] [Transform] Validation failed:', validation.errors);
      return false;
    }
    
    const { yxml, doc } = context;
    const targetIds = Array.isArray(this.target) ? this.target : [this.target];
    const suggestionMode = context.metadata?.suggestionMode !== false; // Default to true for change tracking
    
    log.log('[AI_TOOL_LOG] [Transform] Mode selection:', {
      suggestionMode,
      transformationType: Object.keys(this.transformation)[0],
      changeTrackingEnabled: true
    });
    
    // Find all target nodes
    const targets: Y.XmlElement[] = [];
    for (const id of targetIds) {
      const found = DocumentUtils.findNodeById(yxml, id);
      if (found) targets.push(found.element);
    }
    
    if (targets.length === 0) {
      log.warn('[AI_TOOL_LOG] [Transform] No target nodes found');
      return false;
    }
    
    // Perform transformations in a transaction
    doc.transact(() => {
      if (suggestionMode) {
        // ✅ FIX: Handle multiple targets correctly - mark all as old, create single replacement
        
        // First, mark ALL target elements as old
        targets.forEach(element => {
          element.setAttribute('change_status', 'old');
          element.setAttribute('revision_id', this.revisionId);
        });
        
        log.log('[AI_TOOL_LOG] [Transform] Marked all targets as old:', {
          targetCount: targets.length,
          revisionId: this.revisionId
        });
        
        // Find insertion point (after the last target element)
        const lastTarget = targets[targets.length - 1];
        const parent = lastTarget.parent as Y.XmlFragment;
        let insertIndex = -1;
        for (let i = 0; i < parent.length; i++) {
          if (parent.get(i) === lastTarget) {
            insertIndex = i;
            break;
          }
        }
        
        if (insertIndex === -1) {
          log.error('[AI_TOOL_LOG] [Transform] Last target element not found in parent');
          return;
        }
        
        // Create new content based on transformation type
        if ('text' in this.transformation) {
          // Handle text transformation for multiple nodes
          let nodeText: string;
          if (typeof this.transformation.text === 'string') {
            nodeText = this.transformation.text;
          } else {
            // For mapping objects, concatenate all mapped texts
            const textParts: string[] = [];
            for (const element of targets) {
              const elementId = element.getAttribute('id');
              if (elementId && this.transformation.text[elementId]) {
                textParts.push(this.transformation.text[elementId]);
              }
            }
            nodeText = textParts.join('\n'); // Join with newlines for readability
          }
          
          if (!nodeText.trim()) {
            log.warn('[AI_TOOL_LOG] [Transform] No valid text content for transformation');
            return;
          }
          
          // Use first target as template for new element
          const firstTarget = targets[0];
          const originalType = firstTarget.getAttribute('type');
          const nodeType = originalType || firstTarget.nodeName || 'paragraph';
          
          // Clean attributes from first target
          const originalAttrs = firstTarget.getAttributes();
          const cleanAttrs = { ...originalAttrs };
          delete cleanAttrs.change_status;
          delete cleanAttrs.revision_id;
          
          const newElement = DocumentUtils.createElement(
            nodeType as NodeType,
            nodeText,
            {
              change_status: 'new',
              revision_id: this.revisionId,
              attrs: cleanAttrs
            }
          );
          
          // Insert single new element after the last target
          parent.insert(insertIndex + 1, [newElement]);
          
          log.log('[AI_TOOL_LOG] [Transform] Created single text replacement:', {
            nodeType,
            textLength: nodeText.length,
            insertIndex: insertIndex + 1,
            targetsReplaced: targets.length
          });
          
        } else if ('richContent' in this.transformation) {
          // Handle rich content transformations with formatting marks
          
          // Use first target as template for new element
          const firstTarget = targets[0];
          const elementType = firstTarget.getAttribute('type') || firstTarget.nodeName;
          const originalType = elementType || 'paragraph';
          const originalAttrs = firstTarget.getAttributes();
          
          // Remove change-tracking attributes from the original element's attributes
          const cleanAttrs = { ...originalAttrs };
          delete cleanAttrs.change_status;
          delete cleanAttrs.revision_id;
          
          // Filter out empty text segments and validate content
          const validRichContent = this.transformation.richContent.filter(item => 
            item.text && typeof item.text === 'string' && item.text.trim().length > 0
          );
          
          if (validRichContent.length === 0) {
            log.warn('[AI_TOOL_LOG] [Transform] No valid rich content found, skipping transformation');
            return;
          }
          
          // Combine all rich content segments into a single text string
          let combinedText = '';
          for (const richItem of validRichContent) {
            combinedText += richItem.text;
          }
          
          if (!combinedText.trim()) {
            log.warn('[AI_TOOL_LOG] [Transform] Combined text is empty, skipping transformation');
            return;
          }
          
          // Create element and text node manually to ensure proper mark application
          const newElement = DocumentUtils.createElement(originalType as any, '', {
            change_status: 'new',
            revision_id: this.revisionId
          });
          
          // Apply original attributes (except change tracking ones)
          Object.entries(cleanAttrs).forEach(([key, value]) => {
            if (value !== undefined) {
              newElement.setAttribute(key, value);
            }
          });
          
          // Create text node and insert text content first, then apply marks
          const textNode = new Y.XmlText();

          log.log('[AI_TOOL_LOG] [Transform] DEBUG: Manual element creation:', {
            elementType: originalType || 'paragraph',
            textLength: combinedText.length,
            segmentCount: validRichContent.length
          });
          
          // 1) Insert the text content (decode HTML entities first)
          const decodedText = combinedText; // Using text directly - entities fixed at proper boundary
          textNode.insert(0, decodedText);

          // 2) Apply marks to specific text segments using correct length parameter
          let currentOffset = 0;
          for (const richItem of validRichContent) {
            const text = richItem.text;
            const marks = richItem.marks || [];

            for (const mark of marks) {
              // Build mark attributes based on mark type
              const markAttrs: Record<string, any> = {};

              if (mark.type === 'bold') {
                markAttrs.strong = true;
              } else if (mark.type === 'italic') {
                markAttrs.em = true;
              } else if (mark.type === 'underline') {
                markAttrs.underline = true;
              } else if (mark.type === 'fontSize' && mark.attrs?.size) {
                markAttrs.fontSize = mark.attrs.size;
              } else if (mark.type === 'fontFamily' && mark.attrs?.family) {
                markAttrs.fontFamily = mark.attrs.family;
              } else if (mark.type === 'color' && mark.attrs?.color) {
                markAttrs.color = mark.attrs.color;
              } else if (mark.type === 'backgroundColor' && mark.attrs?.backgroundColor) {
                markAttrs.backgroundColor = mark.attrs.backgroundColor;
              } else if (mark.type === 'link' && mark.attrs?.href) {
                markAttrs.link = mark.attrs.href;
              }

              if (Object.keys(markAttrs).length > 0) {
                log.log('[AI_TOOL_LOG] [Transform] DEBUG: About to apply format:', {
                  startOffset: currentOffset,
                  length: text.length,
                  markAttrs,
                  textLength: text.length,
                  totalTextLength: combinedText.length
                });

                // Correct signature: (start, length, attrs)
                textNode.format(currentOffset, text.length, markAttrs);

                log.log('[AI_TOOL_LOG] [Transform] Applied mark to text:', {
                  markType: mark.type,
                  markAttrs,
                  textSegment: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
                  offset: currentOffset,
                  length: text.length,
                  formatApplied: true
                });
              }
            }

            currentOffset += text.length;
          }

          // 3) Attach the text node to the element
          newElement.insert(0, [textNode]);
          
          log.log('[AI_TOOL_LOG] [Transform] DEBUG: Final element state:', {
            elementType: newElement.nodeName,
            childrenCount: newElement.length,
            textNodeLength: combinedText.length,
            changeStatus: newElement.getAttribute('change_status')
          });
          
          // Insert single new element after the last target
          parent.insert(insertIndex + 1, [newElement]);
          
          log.log('[AI_TOOL_LOG] [Transform] Created single rich content replacement:', {
            originalType: originalType || 'paragraph',
            combinedTextLength: combinedText.length,
            validSegmentCount: validRichContent.length,
            totalMarks: validRichContent.reduce((sum, item) => sum + (item.marks?.length || 0), 0),
            targetsReplaced: targets.length
          });
          
                 } else if ('type' in this.transformation) {
           // For type changes, transform each element individually
           const typeTransformation = this.transformation as { type: NodeType };
           targets.forEach(element => {
             const transformParent = element.parent as Y.XmlFragment;
             let index = -1;
             for (let i = 0; i < transformParent.length; i++) {
               if (transformParent.get(i) === element) {
                 index = i;
                 break;
               }
             }
             
             if (index !== -1) {
               const newElement = DocumentUtils.createElement(
                 typeTransformation.type,
                 DocumentUtils.getTextContent(element),
                 {
                   change_status: 'new',
                   revision_id: this.revisionId,
                   attrs: element.getAttributes()
                 }
               );
               
               transformParent.insert(index + 1, [newElement]);
             }
           });
           
         } else if ('attrs' in this.transformation) {
           // For attribute changes, update each element individually
           const attrsTransformation = this.transformation as { attrs: Record<string, any> };
           targets.forEach(element => {
             Object.entries(attrsTransformation.attrs).forEach(([key, value]) => {
               if (value === null) {
                 element.removeAttribute(key);
               } else {
                 element.setAttribute(key, value);
               }
             });
           });
           
         } else if ('fn' in this.transformation) {
           // For custom transformations, apply to each element
           const fnTransformation = this.transformation as { fn: (element: Y.XmlElement) => void };
           targets.forEach(element => {
             fnTransformation.fn(element);
           });
         }
        
      } else {
        // Direct transformation (non-suggestion mode) - handle each target individually
        targets.forEach(element => {
          if ('text' in this.transformation) {
            let nodeText: string;
            if (typeof this.transformation.text === 'string') {
              nodeText = this.transformation.text;
            } else {
              const elementId = element.getAttribute('id');
              if (!elementId) {
                log.warn('[AI_TOOL_LOG] [Transform] Element has no ID for mapping');
                return;
              }
              nodeText = this.transformation.text[elementId];
              if (!nodeText) {
                log.warn('[AI_TOOL_LOG] [Transform] No text mapping found for element:', elementId);
                return;
              }
            }
            
            // Replace all text content
            element.delete(0, element.length);
            const tn = new Y.XmlText();
            tn.insert(0, nodeText);
            element.insert(0, [tn]);
            
          } else if ('richContent' in this.transformation) {
            // Handle rich content in non-suggestion mode
            const validRichContent = this.transformation.richContent.filter(item => 
              item.text && typeof item.text === 'string' && item.text.trim().length > 0
            );
            
            if (validRichContent.length === 0) {
              log.warn('[AI_TOOL_LOG] [Transform] No valid rich content found for direct transformation');
              return;
            }
            
            // Combine all rich content segments into a single text string
            let combinedText = '';
            for (const richItem of validRichContent) {
              combinedText += richItem.text;
            }
            
            if (!combinedText.trim()) {
              log.warn('[AI_TOOL_LOG] [Transform] Combined text is empty for direct transformation');
              return;
            }
            
            // Replace all content in the element
            element.delete(0, element.length);
            
            // Create new text node with marks
            const textNode = new Y.XmlText(combinedText);
            const combinedTextLength = combinedText.length;  // ✅ Track length separately
            
            log.log('[AI_TOOL_LOG] [Transform] DEBUG: Direct mode text node created:', {
              combinedTextLength: combinedTextLength
            });
            
            // Apply marks to the text node for specific segments
            let currentOffset = 0;
            for (const richItem of validRichContent) {
              const text = richItem.text;
              const marks = richItem.marks || [];
              
              for (const mark of marks) {
                // Build mark attributes based on mark type
                const markAttrs: Record<string, any> = {};
                
                if (mark.type === 'bold') {
                  markAttrs.strong = true;
                } else if (mark.type === 'italic') {
                  markAttrs.em = true;
                } else if (mark.type === 'underline') {
                  markAttrs.underline = true;
                } else if (mark.type === 'fontSize' && mark.attrs?.size) {
                  markAttrs.fontSize = mark.attrs.size;
                } else if (mark.type === 'fontFamily' && mark.attrs?.family) {
                  markAttrs.fontFamily = mark.attrs.family;
                } else if (mark.type === 'color' && mark.attrs?.color) {
                  markAttrs.color = mark.attrs.color;
                } else if (mark.type === 'backgroundColor' && mark.attrs?.backgroundColor) {
                  markAttrs.backgroundColor = mark.attrs.backgroundColor;
                } else if (mark.type === 'link' && mark.attrs?.href) {
                  markAttrs.link = mark.attrs.href;
                }
                
                if (Object.keys(markAttrs).length > 0) {
                  log.log('[AI_TOOL_LOG] [Transform] DEBUG: Direct mode about to apply format:', {
                    startOffset: currentOffset,
                    length: text.length,
                    markAttrs,
                    textLength: text.length,
                    combinedTextLength: combinedTextLength  // ✅ Use tracked length
                  });
                  
                  // Correct signature: (start, length, attrs)
                  textNode.format(currentOffset, text.length, markAttrs);
                  
                  log.log('[AI_TOOL_LOG] [Transform] Applied mark to text (direct mode):', {
                    markType: mark.type,
                    markAttrs,
                    textSegment: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
                    offset: currentOffset,
                    length: text.length,
                    formatApplied: true
                  });
                }
              }
              
              currentOffset += text.length;
            }
            
            log.log('[AI_TOOL_LOG] [Transform] DEBUG: Direct mode final text node state:', {
              combinedTextLength: combinedTextLength,  // ✅ Use tracked length
              aboutToInsert: true
            });
            
            element.insert(0, [textNode]);
            
            log.log('[AI_TOOL_LOG] [Transform] Applied rich content directly:', {
              validSegmentCount: validRichContent.length,
              combinedTextLength: combinedText.length,
              totalMarks: validRichContent.reduce((sum, item) => sum + (item.marks?.length || 0), 0)
            });
            
                     } else if ('type' in this.transformation) {
             // Change node type (Y.js doesn't support this directly, so we recreate)
             const typeTransformation = this.transformation as { type: NodeType };
             const parent = element.parent as Y.XmlFragment;
             let index = -1;
             for (let i = 0; i < parent.length; i++) {
               if (parent.get(i) === element) {
                 index = i;
                 break;
               }
             }
             
             if (index !== -1) {
               const newElement = DocumentUtils.createElement(
                 typeTransformation.type,
                 DocumentUtils.getTextContent(element),
                 {
                   id: element.getAttribute('id'),
                   attrs: element.getAttributes()
                 }
               );
               
               parent.delete(index, 1);
               parent.insert(index, [newElement]);
             }
           } else if ('attrs' in this.transformation) {
             // Update attributes
             const attrsTransformation = this.transformation as { attrs: Record<string, any> };
             Object.entries(attrsTransformation.attrs).forEach(([key, value]) => {
               if (value === null) {
                 element.removeAttribute(key);
               } else {
                 element.setAttribute(key, value);
               }
             });
           } else if ('fn' in this.transformation) {
             // Custom transformation
             const fnTransformation = this.transformation as { fn: (element: Y.XmlElement) => void };
             fnTransformation.fn(element);
           }
        });
      }
    });
    
    log.log('[AI_TOOL_LOG] [Transform] Command executed successfully:', {
      nodesProcessed: targets.length,
      suggestionMode,
      revisionId: this.revisionId
    });
    
    return true;
  }
  
  describe(): string {
    const targetDesc = Array.isArray(this.target) 
      ? `${this.target.length} nodes`
      : `node ${this.target}`;
      
    if ('text' in this.transformation) {
      return `Change text of ${targetDesc}`;
    } else if ('richContent' in this.transformation) {
      const segmentCount = this.transformation.richContent.length;
      const totalMarks = this.transformation.richContent.reduce((sum, item) => sum + (item.marks?.length || 0), 0);
      return `Apply rich formatting to ${targetDesc} (${segmentCount} segments, ${totalMarks} marks)`;
    } else if ('type' in this.transformation) {
      return `Change ${targetDesc} to ${this.transformation.type}`;
    } else if ('attrs' in this.transformation) {
      return `Update attributes of ${targetDesc}`;
    } else {
      return `Transform ${targetDesc}`;
    }
  }
}

/**
 * Enhanced Move command with AI-specific features
 */
export class Move implements DocumentCommand {
  private revisionId: string;
  
  constructor(
    private source: NodeId | NodeId[],
    private destination:
      | { after: NodeId }           // Move after a specific node
      | { before: NodeId }          // Move before a specific node  
      | { at: 'start' | 'end' }     // Move to document start/end
      | { index: number },          // Move to specific index
    options: {
      revisionId?: string;
      suggestionMode?: boolean;
    } = {}
  ) {
    this.revisionId = options.revisionId || `rev_${nanoid(12)}`;
    log.log('[AI_TOOL_LOG] [Move] Command created:', {
      source,
      destination,
      revisionId: this.revisionId,
      suggestionMode: options.suggestionMode
    });
  }
  
  validate(context: CommandContext): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    const sourceIds = Array.isArray(this.source) ? this.source : [this.source];
    for (const id of sourceIds) {
      const validation = DocumentUtils.validateNodeId(id);
      if (!validation.isValid) {
        errors.push(`Invalid source node ID: ${validation.error}`);
      }
    }
    
    // Validate destination
    if ('after' in this.destination) {
      const validation = DocumentUtils.validateNodeId(this.destination.after);
      if (!validation.isValid) {
        errors.push(`Invalid destination 'after' node ID: ${validation.error}`);
      }
    } else if ('before' in this.destination) {
      const validation = DocumentUtils.validateNodeId(this.destination.before);
      if (!validation.isValid) {
        errors.push(`Invalid destination 'before' node ID: ${validation.error}`);
      }
    }
    
    return { isValid: errors.length === 0, errors };
  }
  
  execute(context: CommandContext): boolean {
    log.log('[AI_TOOL_LOG] [Move] Executing command:', {
      source: this.source,
      destination: this.destination,
      revisionId: this.revisionId
    });
    
    // Validate before execution
    const validation = this.validate(context);
    if (!validation.isValid) {
      log.error('[AI_TOOL_LOG] [Move] Validation failed:', validation.errors);
      return false;
    }
    const { yxml, doc } = context;
    const sourceIds = Array.isArray(this.source) ? this.source : [this.source];
    
    // Find all source nodes and their locations
    const sources: NodeLocation[] = [];
    for (const id of sourceIds) {
      const found = DocumentUtils.findNodeById(yxml, id);
      if (found) sources.push(found);
    }
    
    if (sources.length === 0) {
      log.warn('[AI_TOOL_LOG] [Move] No source nodes found');
      return false;
    }
    
    // Determine destination index
    let destIndex: number;
    
    if ('after' in this.destination) {
      const found = DocumentUtils.findNodeById(yxml, this.destination.after);
      if (!found) {
        log.error('[AI_TOOL_LOG] [Move] Destination node not found for after location:', this.destination.after);
        return false;
      }
      destIndex = found.index + 1;
    } else if ('before' in this.destination) {
      const found = DocumentUtils.findNodeById(yxml, this.destination.before);
      if (!found) {
        log.error('[AI_TOOL_LOG] [Move] Destination node not found for before location:', this.destination.before);
        return false;
      }
      destIndex = found.index;
    } else if ('at' in this.destination) {
      destIndex = this.destination.at === 'start' ? 0 : yxml.length;
    } else {
      destIndex = Math.max(0, Math.min(this.destination.index, yxml.length));
    }
    
    // Perform move in a transaction
    doc.transact(() => {
      // Sort sources by index descending to avoid position shifts during removal
      const sortedSources = [...sources].sort((a, b) => b.index - a.index);
      
      // Calculate how many items will be removed before the destination
      let offsetAdjustment = 0;
      for (const source of sortedSources) {
        if (source.parent === yxml && source.index < destIndex) {
          offsetAdjustment++;
        }
      }
      
      // Extract elements (remove from their current positions)
      const elements: Y.XmlElement[] = [];
      for (const source of sortedSources) {
        elements.unshift(source.element); // Add to beginning to maintain order
        source.parent.delete(source.index, 1);
      }
      
      // Adjust destination index based on removals
      const adjustedDestIndex = Math.max(0, destIndex - offsetAdjustment);
      
      // Insert at new location
      yxml.insert(adjustedDestIndex, elements);
    });
    
    log.log('[AI_TOOL_LOG] [Move] Command executed successfully:', {
      nodesProcessed: sources.length,
      destinationIndex: destIndex,
      revisionId: this.revisionId
    });
    
    return true;
  }
  
  describe(): string {
    const sourceDesc = Array.isArray(this.source)
      ? `${this.source.length} nodes`
      : `node ${this.source}`;
      
    if ('after' in this.destination) {
      return `Move ${sourceDesc} after node ${this.destination.after}`;
    } else if ('before' in this.destination) {
      return `Move ${sourceDesc} before node ${this.destination.before}`;
    } else if ('at' in this.destination) {
      return `Move ${sourceDesc} to ${this.destination.at}`;
    } else {
      return `Move ${sourceDesc} to index ${this.destination.index}`;
    }
  }
}

/**
 * Reading Commands - Non-mutating operations for document inspection
 */

/**
 * Result of reading operations
 */
export interface ReadResult {
  nodes: Array<{
    id: NodeId;
    type: NodeType;
    text: string;
    attrs?: Record<string, any>;
    children?: ReadResult['nodes'];
  }>;
  metadata?: {
    totalNodes: number;
    chunkIndex?: number;
    totalChunks?: number;
  };
}

/**
 * Read document content with various strategies
 */
export class Read {
  /**
   * Read all nodes in the document (use with caution on large docs)
   */
  static all(context: CommandContext): ReadResult {
    // 🔍 TAB_DEBUG: Log existing document content to compare with AI-generated
    try {
      const yxml = context.yxml;
      if (yxml && yxml.length > 0) {
        // Check first few nodes for tabs
        for (let i = 0; i < Math.min(yxml.length, 5); i++) {
          const element = yxml.get(i);
          if (element && element instanceof Y.XmlElement && element.nodeName === 'paragraph') {
            const textContent = DocumentUtils.getTextContent(element);
            const hasLeading = /^\s+/.test(textContent);
            
            if (hasLeading) {
              const leading = textContent.match(/^\s+/)?.[0] || '';
              const charCodes = leading.split('').map(c => c.charCodeAt(0));
              
              log.log('🔍 [TAB_DEBUG] [Read-Existing] Found paragraph with leading whitespace:', {
                nodeIndex: i,
                leadingCharCount: leading.length,
                leadingCharCodes: charCodes,
                isSingleTab: charCodes.length === 1 && charCodes[0] === 9,
                isFourSpaces: charCodes.length === 4 && charCodes.every(c => c === 32),
                textPreview: JSON.stringify(textContent.substring(0, 50)),
                source: 'EXISTING_DOCUMENT',
                note: 'This shows what manual Tab looks like vs AI-generated'
              });
            }
          }
        }
      }
    } catch (error) {
      // Don't break reading if debug fails
    }
    
    // Use centralized service for consistency
    const documentStructure = yDocumentService.getDocumentStructure(
      context.doc,
      Number.MAX_SAFE_INTEGER, // Get all nodes
      { docId: context.docId, logPrefix: '[Read.all]' }
    );
    
    // ✅ FIX: Fall back to direct reading if service fails OR returns empty nodes
    if (!documentStructure || !documentStructure.nodes || documentStructure.nodes.length === 0) {
      // Document structure fallback debug removed to reduce spam
      
      // Fallback to old method if service fails or returns empty
      const nodes = this.readFragment(context.yxml)
        // 🚨 COMPREHENSIVE FILTER: Exclude ALL outdated content for Claude
        .filter((n: any) => {
          const changeStatus = n?.attrs?.change_status;
          const shouldExclude = changeStatus === 'old' || 
                               changeStatus === 'deleted' || 
                               changeStatus === 'replaced';
          
          if (shouldExclude) {
            log.log(`🚫 [READ_ALL_FILTER] Excluding outdated node:`, {
              nodeId: n.id,
              changeStatus,
              reason: 'outdated_content_filtered_at_source'
            });
          }
          
          return !shouldExclude;
        });
      // Direct YJS reading result debug removed to reduce spam
      
      return {
        nodes,
        metadata: {
          totalNodes: nodes.length
        }
      };
    }
    
    // Convert from YDocumentNode to ReadResult node format
    const nodes = documentStructure.nodes.map(node => ({
      id: node.id,
      type: node.type as NodeType,
      text: node.text,
      attrs: node.attributes
    }))
    // Exclude deletions from read/context
    .filter((n: any) => !(n?.attrs?.change_status === 'old'));
    
    // log.log('[Read.all] ✅ Using documentStructure service result:', {
    //   nodesFound: nodes.length,
    //   totalFromService: documentStructure.totalNodes
    // });
    
    return {
      nodes,
      metadata: {
        totalNodes: nodes.length
      }
    };
  }
  
  /**
   * Read a specific chunk of the document
   */
  static chunk(
    context: CommandContext, 
    options: { 
      chunkSize?: number; 
      chunkIndex?: number;
      strategy?: 'nodes' | 'words';
    } = {}
  ): ReadResult {
    const { chunkSize = 10, chunkIndex = 0, strategy = 'nodes' } = options;
    
    if (strategy === 'nodes') {
      // Chunk by number of nodes
      const allNodes = this.readFragment(context.yxml);
      const totalChunks = Math.ceil(allNodes.length / chunkSize);
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, allNodes.length);
      
      return {
        nodes: allNodes.slice(start, end),
        metadata: {
          totalNodes: allNodes.length,
          chunkIndex,
          totalChunks
        }
      };
    } else {
      // Chunk by word count (useful for token limits)
      const allNodes = this.readFragment(context.yxml);
      let currentChunk: typeof allNodes = [];
      let currentChunkWords = 0;
      let chunkCount = 0;
      
      for (const node of allNodes) {
        const nodeWords = node.text.split(/\s+/).filter((w: string) => w.trim()).length;
        
        if (currentChunkWords + nodeWords > chunkSize && currentChunk.length > 0) {
          // Start new chunk
          chunkCount++;
          if (chunkCount - 1 === chunkIndex) {
            break; // We have our chunk
          }
          currentChunk = [];
          currentChunkWords = 0;
        }
        
        if (chunkCount === chunkIndex) {
          currentChunk.push(node);
          currentChunkWords += nodeWords;
        }
      }
      
      // Calculate total chunks based on total word count
      const totalWords = allNodes.reduce((sum: number, n: any) => {
        return sum + n.text.split(/\s+/).filter((w: string) => w.trim()).length;
      }, 0);
      
      return {
        nodes: currentChunk,
        metadata: {
          totalNodes: allNodes.length,
          chunkIndex,
          totalChunks: Math.ceil(totalWords / chunkSize)
        }
      };
    }
  }
  
  /**
   * Read a specific node by ID
   */
  static byId(context: CommandContext, nodeId: NodeId): ReadResult {
    const location = DocumentUtils.findNodeById(context.yxml, nodeId);
    
    if (!location) {
      return { nodes: [] };
    }
    
    const node = this.elementToNode(location.element);
    // Exclude deletions
    if ((node as any)?.attrs?.change_status === 'old') {
      return { nodes: [] };
    }
    return {
      nodes: [node],
      metadata: {
        totalNodes: 1
      }
    };
  }
  
  /**
   * Read nodes within a range
   */
  static range(context: CommandContext, fromId: NodeId, toId: NodeId): ReadResult {
    const fromLocation = DocumentUtils.findNodeById(context.yxml, fromId);
    const toLocation = DocumentUtils.findNodeById(context.yxml, toId);
    
    if (!fromLocation || !toLocation || fromLocation.parent !== toLocation.parent) {
      return { nodes: [] };
    }
    
    const parent = fromLocation.parent;
    const startIdx = Math.min(fromLocation.index, toLocation.index);
    const endIdx = Math.max(fromLocation.index, toLocation.index);
    
    let nodes: ReadResult['nodes'] = [];
    
    for (let i = startIdx; i <= endIdx; i++) {
      const element = parent.get(i);
      if (element instanceof Y.XmlElement) {
        nodes.push(this.elementToNode(element));
      }
    }
    
    // Exclude deletions
    nodes = nodes.filter((n: any) => !(n?.attrs?.change_status === 'old'));
    return {
      nodes,
      metadata: {
        totalNodes: nodes.length
      }
    };
  }
  
  /**
   * Search for nodes containing specific text
   */
  static search(
    context: CommandContext, 
    query: string, 
    options: { caseSensitive?: boolean; limit?: number; fuzzy?: boolean; fuzzyThreshold?: number } = {}
  ): ReadResult {
    const { caseSensitive = false, limit = 50, fuzzy = true, fuzzyThreshold = 0.8 } = options;
    const searchQuery = caseSensitive ? query : query.toLowerCase();
    let matches: ReadResult['nodes'] = [];
    
    const searchInFragment = (fragment: Y.XmlFragment | Y.XmlElement) => {
      for (let i = 0; i < fragment.length && matches.length < limit; i++) {
        const child = fragment.get(i);
        
        if (child instanceof Y.XmlElement) {
          const text = DocumentUtils.getTextContent(child);
          
          let isMatch = false;
          if (fuzzy) {
            // Use fuzzy matching
            isMatch = fuzzyMatch(text, query, fuzzyThreshold);
          } else {
            // Use exact substring matching
            const searchText = caseSensitive ? text : text.toLowerCase();
            isMatch = searchText.includes(searchQuery);
          }
          
          if (isMatch) {
            matches.push(this.elementToNode(child));
          }
          
          // Recursively search children
          if (matches.length < limit) {
            searchInFragment(child);
          }
        }
      }
    };
    
    searchInFragment(context.yxml);
    
    // Exclude deletions
    matches = matches.filter((n: any) => !(n?.attrs?.change_status === 'old'));
    
    return {
      nodes: matches,
      metadata: {
        totalNodes: matches.length
      }
    };
  }
  
  /**
   * Get document statistics
   */
  static stats(context: CommandContext): {
    totalNodes: number;
    nodesByType: Record<NodeType, number>;
    totalCharacters: number;
    totalWords: number;
  } {
    // Use centralized service for consistency
    const stats = yDocumentService.getDocumentStats(
      context.doc,
      { docId: context.docId, logPrefix: '[Read.stats]' }
    );
    
    if (!stats) {
      // Fallback to old method if service fails
      let totalNodes = 0;
      let totalCharacters = 0;
      let totalWords = 0;
      const nodesByType: Record<string, number> = {};
      
      const analyze = (fragment: Y.XmlFragment | Y.XmlElement) => {
        for (let i = 0; i < fragment.length; i++) {
          const child = fragment.get(i);
          
          if (child instanceof Y.XmlElement) {
            totalNodes++;
            const type = child.getAttribute('type') || 'unknown';
            nodesByType[type] = (nodesByType[type] || 0) + 1;
            
            const text = DocumentUtils.getTextContent(child);
            totalCharacters += text.length;
            totalWords += text.split(/\s+/).filter(word => word.length > 0).length;
            
            // Recursively analyze children
            analyze(child);
          }
        }
      };
      
      analyze(context.yxml);
      
      return {
        totalNodes,
        nodesByType: nodesByType as Record<NodeType, number>,
        totalCharacters,
        totalWords
      };
    }
    
    return stats;
  }

  /**
   * Helper: Read all nodes from a fragment
   */
  private static readFragment(fragment: Y.XmlFragment | Y.XmlElement, options: { flattenPages?: boolean } = {}): ReadResult['nodes'] {
    const nodes: ReadResult['nodes'] = [];
    const { flattenPages = true } = options;
    
    // Fragment reading debug removed to reduce spam
    
    for (let i = 0; i < fragment.length; i++) {
      const child = fragment.get(i);
      log.log('[readFragment] Processing child', i, ':', {
        childType: child?.constructor.name,
        isXmlElement: child instanceof Y.XmlElement,
        childNodeName: (child as any)?.nodeName
      });
      
      if (child instanceof Y.XmlElement) {
        const node = this.elementToNode(child);
        nodes.push(node);
        log.log('[readFragment] ✅ Added node:', {
          id: node.id,
          type: node.type,
          textLength: node.text?.length || 0
        });
        
        // ✅ NEW: If this is a page and we want flattened reading, include its children
        if (flattenPages && child.nodeName === 'page' && child.length > 0) {
          log.log('[readFragment] 📄 Reading page children:', {
            pageId: node.id,
            childCount: child.length
          });
          
          for (let j = 0; j < child.length; j++) {
            const pageChild = child.get(j);
            if (pageChild instanceof Y.XmlElement) {
              const childNode = this.elementToNode(pageChild);
              // Mark this node as belonging to the page for debugging
              childNode.attrs = { ...childNode.attrs, parentPageId: node.id };
              nodes.push(childNode);
              
              log.log('[readFragment] ✅ Added page child:', {
                id: childNode.id,
                type: childNode.type,
                parentPageId: node.id,
                textLength: childNode.text?.length || 0
              });
            }
          }
        }
      }
    }
    
    // Fragment final result debug removed to reduce spam
    
    return nodes;
  }

  /**
   * Helper: Convert Y.XmlElement to our node format
   * Properly extracts type and attributes from Y.js XML elements
   */
  private static elementToNode(element: Y.XmlElement): ReadResult['nodes'][0] {
    // Get all attributes at once for better performance
    const allAttrs = element.getAttributes() || {};
    
    // Get the element type from nodeName (the actual HTML tag)
    const elementTagName = element.nodeName || 'p';
    
    // Map HTML tag names to our internal node types
    const getNodeType = (tagName: string): NodeType => {
      const lowerTagName = tagName.toLowerCase();
      switch (lowerTagName) {
        // Y.js semantic names (preferred)
        case 'heading': return 'heading';
        case 'paragraph': return 'paragraph';
        case 'list': return 'bullet_list';
        case 'list_item':
        case 'listitem': return 'list_item';
        case 'code_block':
        case 'codeblock': return 'code_block';
        // HTML tag names (fallback)
        case 'h1': case 'h2': case 'h3':
        case 'h4': case 'h5': case 'h6': return 'heading';
        case 'ul': return 'bullet_list';
        case 'ol': return 'ordered_list';
        case 'li': return 'list_item';
        case 'pre': case 'code': return 'code_block';
        // Table element mappings
        case 'table': return 'table';
        case 'tr': return 'table_row';
        case 'td': return 'table_cell';
        case 'th': return 'table_header';
        case 'p': case 'div':
        default: return 'paragraph';
      }
    };
    
    const nodeType = getNodeType(elementTagName);
    
    // Get or assign ID
    let actualId = allAttrs.id;
    if (!actualId || actualId === 'undefined') {
      actualId = `node_${nanoid(12)}`;
      element.setAttribute('id', actualId);
    }
    
    // Get text content
    const textContent = DocumentUtils.getTextContent(element);
    
    const node: ReadResult['nodes'][0] = {
      id: actualId,
      type: nodeType,
      text: textContent,
    };
    
    // Add non-standard attributes (excluding id since we handle it separately)
    const customAttrs: Record<string, any> = {};
    for (const [key, value] of Object.entries(allAttrs)) {
      if (key !== 'id') {
        customAttrs[key] = value;
      }
    }
    
    // Add HTML-specific attributes that help define the node
    if (elementTagName.match(/^h[1-6]$/i)) {
      const level = parseInt(elementTagName.charAt(1));
      customAttrs.level = level;
    }
    
    if (Object.keys(customAttrs).length > 0) {
      node.attrs = customAttrs;
    }
    
    // Add children if this is a container node (like lists)
    if (element.length > 0) {
      const firstChild = element.get(0);
      if (firstChild instanceof Y.XmlElement) {
        node.children = [];
        for (let i = 0; i < element.length; i++) {
          const child = element.get(i);
          if (child instanceof Y.XmlElement) {
            node.children.push(this.elementToNode(child));
          }
        }
      }
    }
    
    return node;
  }
}

/**
 * Executor for document commands with batching and streaming support
 */
/**
 * Enhanced Command Executor with AI-specific features
 */
export class CommandExecutor {
  private pendingCommands: DocumentCommand[] = [];
  private flushTimeout?: NodeJS.Timeout;
  private flushDelay: number = 50; // milliseconds

  constructor(private context: CommandContext) {
    log.log('[AI_TOOL_LOG] [CommandExecutor] Created executor for document:', {
      docId: context.docId,
      suggestionMode: context.metadata?.suggestionMode
    });
  }

  /**
   * Execute a single command with validation and logging
   */
  execute(command: DocumentCommand): boolean {
    log.log('[AI_TOOL_LOG] [CommandExecutor] Executing command:', command.describe?.() || 'Unknown command');
    const traceId = (this.context.metadata as any)?.traceId;
    if (traceId) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      traceLog({ traceId, type: 'command_execute', data: { describe: command.describe?.() } });
    }
    
    // Validate command if possible
    if (command.validate) {
      const validation = command.validate(this.context);
      if (!validation.isValid) {
        log.error('[AI_TOOL_LOG] [CommandExecutor] Command validation failed:', validation.errors);
        if (traceId) {
          traceLog({ traceId, type: 'command_validate_failed', data: { errors: validation.errors } });
        }
        return false;
      }
      if (traceId) {
        traceLog({ traceId, type: 'command_validate_ok', data: { describe: command.describe?.() } });
      }
    }
    
    const success = command.execute(this.context);
    if (traceId) {
      traceLog({ traceId, type: 'command_execute_done', data: { describe: command.describe?.(), success } });
    }
    log.log('[AI_TOOL_LOG] [CommandExecutor] Command execution result:', {
      success,
      description: command.describe?.() || 'Unknown command'
    });
    
    return success;
  }
  
  /**
   * Queue a command for batched execution
   */
  queue(command: DocumentCommand): void {
    this.pendingCommands.push(command);
    this.scheduleFlush();
  }
  
  /**
   * Execute multiple commands as a batch with enhanced logging
   */
  executeBatch(commands: DocumentCommand[]): boolean {
    if (commands.length === 0) {
      log.log('[AI_TOOL_LOG] [CommandExecutor] Empty batch, skipping');
      return true;
    }
    
    log.log('[AI_TOOL_LOG] [CommandExecutor] Executing batch:', {
      commandCount: commands.length,
      commands: commands.map(cmd => cmd.describe?.() || 'Unknown command')
    });
    
    // Validate all commands first
    for (const [index, command] of commands.entries()) {
      if (command.validate) {
        const validation = command.validate(this.context);
        if (!validation.isValid) {
          log.error('[AI_TOOL_LOG] [CommandExecutor] Batch validation failed at command', index, ':', validation.errors);
          return false;
        }
      }
    }
    
    // Execute all commands in a single transaction
    let success = true;
    let executedCount = 0;
    const { doc } = this.context;
    
    doc.transact(() => {
      for (const command of commands) {
        if (!command.execute(this.context)) {
          success = false;
          log.error('[AI_TOOL_LOG] [CommandExecutor] Command failed in batch:', command.describe?.() || 'Unknown command');
          break;
        }
        executedCount++;
      }
    });
    
    log.log('[AI_TOOL_LOG] [CommandExecutor] Batch execution completed:', {
      success,
      executedCount,
      totalCount: commands.length
    });
    
    return success;
  }
  
  /**
   * Execute commands as a stream for large operations
   */
  async *executeStream(
    commands: DocumentCommand[],
    options: { batchSize?: number; yieldEvery?: number } = {}
  ): AsyncGenerator<{ completed: number; total: number }> {
    const { batchSize = 10, yieldEvery = 100 } = options;
    let completed = 0;
    
    for (let i = 0; i < commands.length; i += batchSize) {
      const batch = commands.slice(i, Math.min(i + batchSize, commands.length));
      this.executeBatch(batch);
      
      completed += batch.length;
      
      // Yield progress and allow UI to breathe
      if (completed % yieldEvery === 0 || completed === commands.length) {
        yield { completed, total: commands.length };
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
    }
  }
  
  /**
   * Flush all pending commands
   */
  flush(): boolean {
    if (this.pendingCommands.length === 0) return false;
    
    const commands = this.pendingCommands;
    this.pendingCommands = [];
    
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = undefined;
    }
    
    return this.executeBatch(commands);
  }
  
  private scheduleFlush(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }
    
    this.flushTimeout = setTimeout(() => {
      this.flush();
    }, this.flushDelay);
  }
}

/**
 * Factory functions for common command patterns
 */
export const Commands = {
  // Insert commands
  insertParagraph: (text: string, location: Insert['location']) =>
    new Insert([{ type: 'paragraph', text }], location),
    
  insertHeading: (text: string, level: number, location: Insert['location']) =>
    new Insert([{ 
      type: 'heading', 
      text, 
      metadata: { attrs: { level } } 
    }], location),
    
  insertMultiple: (content: Insert['content'], location: Insert['location']) =>
    new Insert(content, location),
    
  // Remove commands  
  removeNode: (id: NodeId) =>
    new Remove({ id }),
    
  removeRange: (from: NodeId, to: NodeId) =>
    new Remove({ from, to }),
    
  clearDocument: () =>
    new Remove({ all: true }),
    
  // Transform commands
  changeText: (nodeId: NodeId, text: string) =>
    new Transform(nodeId, { text }),
    
  changeRichText: (nodeId: NodeId, richContent: Array<{ text: string; marks?: Array<{ type: string; attrs?: Record<string, any> }> }>) =>
    new Transform(nodeId, { richContent }),
    
  changeType: (nodeId: NodeId, type: NodeType) =>
    new Transform(nodeId, { type }),
    
  updateAttributes: (nodeId: NodeId, attrs: Record<string, any>) =>
    new Transform(nodeId, { attrs }),
    
  // Move commands
  moveNode: (nodeId: NodeId, destination: Move['destination']) =>
    new Move(nodeId, destination),
    
  moveNodes: (nodeIds: NodeId[], destination: Move['destination']) =>
    new Move(nodeIds, destination),
    
  // Read commands (these return results directly, not command objects)
  read: {
    all: (context: CommandContext) => Read.all(context),
    
    chunk: (context: CommandContext, options?: Parameters<typeof Read.chunk>[1]) => 
      Read.chunk(context, options),
      
    byId: (context: CommandContext, nodeId: NodeId) => 
      Read.byId(context, nodeId),
      
    range: (context: CommandContext, fromId: NodeId, toId: NodeId) => 
      Read.range(context, fromId, toId),
      
    search: (context: CommandContext, query: string, options?: Parameters<typeof Read.search>[2]) => 
      Read.search(context, query, options),
      
    stats: (context: CommandContext) => 
      Read.stats(context),
  },
  replaceWithMarkdown: (target: { from: NodeId; to: NodeId }, markdown: string, options?: { revisionId?: string; batchDescription?: string; suggestionMode?: boolean }) =>
    new ReplaceWithMarkdown(target, markdown, options as any)
};

// Moved Markdown-specific replacement logic to `yDocLLMAPI.ts`